import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useApi } from "../../hooks/useApi";
import { Users, User, UserCheck, Search, X, Lightbulb, BarChart3, ChevronLeft, ChevronRight, Edit2, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import PageAnimationWrapper from "../../components/PageAnimationWrapper";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { AntRangePicker, AntDropdown, dayjs } from "../../components/UI";
import { Card, Button, Tag, Progress, Space, Typography, Tooltip, ConfigProvider, theme as antdTheme, Empty, Modal, Input, InputNumber, Form, TimePicker, Skeleton, Row, Col } from "antd";
import { EditOutlined, DeleteOutlined, InfoCircleOutlined, CalendarOutlined, UserOutlined, AimOutlined, CheckCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import GoalFormDrawer from "../../components/GoalFormDrawer/GoalFormDrawer";
import MemberHierarchySearch from "../../components/MemberHierarchySearch";
import { fadeInVariants, pageVariants, pageTransition } from '../../utils/animations';

const { Text, Paragraph } = Typography;

// Glassmorphism styles
const glassCardStyles = `
  .glass-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);
    transition: all 0.3s ease;
  }
  
  .glass-card:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.9);
  }
  
  .glass-card-light {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 20px;
    box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.7);
    transition: all 0.3s ease;
  }
  
  .glass-card-light:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);
  }
  
  .glass-card-interactive {
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    box-shadow: 0 15px 35px -8px rgba(0, 0, 0, 0.6);
    transition: all 0.2s ease;
  }
  
  .glass-card-interactive:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: scale(1.02);
    box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.8);
  }
  
  .glass-card-dark {
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 20px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.9);
    transition: all 0.3s ease;
  }
  
  .glass-card-dark:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-1px);
    box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.95);
  }
  
  .glass-card-dark-interactive {
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    box-shadow: 0 15px 35px -8px rgba(0, 0, 0, 0.7);
    transition: all 0.2s ease;
  }
  
  .glass-card-dark-interactive:hover {
    background: rgba(0, 0, 0, 0.75);
    transform: scale(1.01);
    box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.85);
  }
  
  .glass-card-static {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);
  }
`;

/**
 * Reports Component - Organizational Tree Visualization
 *
 * This component displays the organizational hierarchy in a tree-like structure,
 * showing positions, team members, and their reporting relationships.
 * Features include:
 * - Hierarchical position display
 * - Team member counts (direct/indirect)
 * - Interactive popup for detailed team information
 * - Responsive grid layout
 */
const Reports = () => {
  const { user } = useAuth();
  const api = useApi();

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const { mode } = useTheme();
  const isDark = mode === "dark";

  const treeContainerClass = `rounded-xl p-8 relative overflow-hidden border ${
    isDark
      ? 'bg-zinc-900/50 backdrop-blur border-zinc-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
      : 'bg-white border-slate-200 shadow-sm'
  }`;
  const treeTitleClass = `text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`;
  const treeLegendTextClass = `text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`;
  const legendChipDirectClass = `text-[10px] px-1 rounded ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-indigo-100 text-indigo-600'}`;
  const legendChipIndirectClass = `text-[10px] px-1 rounded ${isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'}`;
  const totalMembersTextClass = `flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`;
  const levelBadgeClass = `rounded-full w-8 h-8 flex items-center justify-center ${
    isDark ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 text-blue-400 backdrop-blur-sm shadow-lg'
           : 'bg-indigo-100 border border-indigo-200 text-indigo-600 shadow-sm'
  }`;
  const positionCardClass = `relative group flex justify-center`;
  const positionCardSurfaceClass = `w-full max-w-[280px] p-4 rounded-lg transition-all duration-200 cursor-pointer border ${
    isDark
      ? 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/70'
      : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm'
  }`;
  const memberRowClass = `flex items-center justify-between rounded px-2 py-1 cursor-pointer transition-all duration-200 border ${
    isDark
      ? 'bg-zinc-800/50 hover:bg-zinc-700/50 border-transparent hover:border-zinc-600'
      : 'bg-slate-100 hover:bg-slate-200 border border-transparent hover:border-slate-300'
  }`;
  const memberNameClass = `text-xs font-medium truncate ${isDark ? 'text-blue-300' : 'text-indigo-600'}`;
  const directBadgeClass = `text-[10px] px-2 py-1 rounded-full transition-all border ${
    isDark
      ? 'bg-blue-500/30 text-blue-200 border-blue-400/20 hover:bg-blue-500/50 hover:text-white hover:border-blue-400/40'
      : 'bg-indigo-100 text-indigo-600 border-indigo-200 hover:bg-indigo-200 hover:text-indigo-700 hover:border-indigo-300'
  }`;
  const indirectBadgeClass = `text-[10px] px-2 py-1 rounded-full transition-all border ${
    isDark
      ? 'bg-purple-500/30 text-purple-200 border-purple-400/20 hover:bg-purple-500/50 hover:text-white hover:border-purple-400/40'
      : 'bg-purple-100 text-purple-600 border-purple-200 hover:bg-purple-200 hover:text-purple-700 hover:border-purple-300'
  }`;
  const emptyMemberBadgeClass = `text-[10px] italic px-3 py-1 rounded-full ${
    isDark ? 'bg-white/5 text-gray-500' : 'bg-slate-100 text-slate-500'
  }`;
  const teamPanelLeftClass = `w-[35%] flex flex-col overflow-hidden min-h-0 border-r ${
    isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-slate-200 bg-slate-50'
  }`;
  const teamPanelHeaderTitleClass = `text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`;
  const teamPanelHeaderSubtitleClass = `text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`;
  const teamTabButtonClass = (active) =>
    `flex-1 py-3 px-4 text-sm font-medium transition-colors border-b-2 ${
      active
        ? isDark
          ? 'text-white border-white bg-white/5'
          : 'text-indigo-600 border-indigo-600 bg-indigo-50'
        : isDark
          ? 'text-gray-400 hover:text-white border-transparent'
          : 'text-slate-500 hover:text-slate-700 border-transparent'
    }`;
  const searchInputClass = `w-full pl-10 pr-10 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/50 transition-all duration-200 ${
    isDark
      ? 'bg-black border-white/10 text-white placeholder-gray-400'
      : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 shadow-sm'
  }`;
  const searchClearButtonClass = `absolute inset-y-0 right-0 pr-3 flex items-center transition-colors ${
    isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
  }`;
  const infoPillClass = `text-xs px-3 py-1 rounded font-medium ${
    isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'
  }`;
  const emptyStateCardClass = `text-center py-8 rounded-xl border ${
    isDark ? 'bg-zinc-900/60 border-zinc-700 text-gray-300' : 'bg-slate-100 border-slate-200 text-slate-600'
  }`;
  const memberSelectedHighlightClass = `bg-gradient-to-r from-indigo-500/20 to-purple-500/30 border-2 shadow-lg ring-2 ring-indigo-400/30 ring-offset-1 ${
    isDark ? 'border-indigo-400 ring-offset-black text-white' : 'border-indigo-200 ring-offset-white text-slate-900'
  }`;
  const memberCardDefaultClass = isDark
    ? 'bg-zinc-900/60 border border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/70 backdrop-blur-sm'
    : 'bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm';
  const viewWorkButtonBaseClass = isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700';
  const teamPanelRightClass = `flex-1 overflow-y-auto min-h-0 ${isDark ? 'bg-transparent' : 'bg-white'}`;
  const summaryCardClass = `rounded-lg p-6 transition-all duration-300 relative group border ${
    isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'
  }`;
  const summaryCardAccentClass = isDark
    ? 'bg-gradient-to-r from-transparent via-blue-500 to-transparent'
    : 'bg-gradient-to-r from-transparent via-indigo-400 to-transparent';
  const panelHeaderSurfaceClass = `p-6 flex-shrink-0 relative z-10 border-b ${
    isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-slate-50 border-slate-200'
  }`;
  const dateSelectorSurfaceClass = `flex items-center justify-center rounded-xl p-3 border ${
    isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-indigo-50 border-indigo-100'
  }`;
  const timelineDotColors = {
    active: isDark ? 'bg-blue-500' : 'bg-indigo-500',
    inactive: isDark ? 'bg-zinc-600' : 'bg-slate-300',
  };
  const timelineDayClass = (isToday, isSelected) => {
    if (isSelected) {
      return isDark
        ? 'bg-blue-600 text-white shadow-lg'
        : 'bg-indigo-600 text-white shadow-md';
    }
    if (isToday) {
      return isDark
        ? 'bg-blue-600/50 text-blue-200 shadow-md'
        : 'bg-indigo-100 text-indigo-600 shadow-sm';
    }
    return isDark
      ? 'hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200'
      : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700';
  };
  const modalSurfaceClass = `border rounded-xl overflow-hidden ${
    isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-slate-200 shadow-xl'
  }`;
  const modalCloseButtonClass = `transition-colors rounded-lg ${
    isDark ? 'text-gray-400 hover:text-white hover:bg-zinc-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
  }`;
  const modalListItemClass = `flex items-center justify-between rounded-lg px-4 py-3 cursor-pointer transition-all duration-200 border ${
    isDark ? 'bg-zinc-800/50 hover:bg-zinc-700/50 border-transparent hover:border-zinc-600' : 'bg-slate-100 hover:bg-slate-200 border border-transparent hover:border-slate-300'
  }`;
  const modalActionButtonClass = `px-4 py-2 rounded-lg transition-colors ${
    isDark ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
  }`;
  const modalAvatarClass = `w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${
    isDark ? 'border-white/20 bg-gradient-to-br from-blue-500/20 to-blue-600/20 text-white' : 'border-slate-200 bg-indigo-100 text-indigo-600'
  }`;

  /** @type {Array} List of all users in the organization */
  const [users, setUsers] = useState([]);

  /** @type {Array} Organizational hierarchy structure with levels and positions */
  const [hierarchy, setHierarchy] = useState([]);

  /** @type {Array} Filtered hierarchy based on search */
  const [filteredHierarchy, setFilteredHierarchy] = useState([]);

  /** @type {string} Global search term for hierarchy */
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");

  /** @type {boolean} Loading state for data fetching */
  const [loading, setLoading] = useState(true);

  /** @type {Object|null} Currently selected member for panel details */
  const [selectedMember, setSelectedMember] = useState(null);

  /** @type {Object|null} Member whose work details are being viewed */
  const [workDetailsMember, setWorkDetailsMember] = useState(null);

  /** @type {boolean} Controls slide-in panel visibility */
  const [showPanel, setShowPanel] = useState(false);

  /** @type {string} Search term for filtering team members */
  const [memberSearchTerm, setMemberSearchTerm] = useState("");

  /** @type {string} Active tab in team reporting ('direct' | 'indirect') */
  const [teamTab, setTeamTab] = useState("direct");

  /** @type {Date} Selected date for filtering */
  const [selectedDate, setSelectedDate] = useState(new Date());

  /** @type {Date} Current month for calendar navigation */
  const [currentMonth, setCurrentMonth] = useState(new Date());

  /** @type {Object|null} Daily summary data for selected user and date */
  const [dailySummaryData, setDailySummaryData] = useState(null);

  /** @type {boolean} Loading state for daily summary data */
  const [dailySummaryLoading, setDailySummaryLoading] = useState(false);

  /** @type {Object} Tracks which positions have their member lists expanded */
  const [expandedPositions, setExpandedPositions] = useState({});

  /** @type {Object|null} Currently selected position for showing all members */
  const [selectedPositionForMembers, setSelectedPositionForMembers] = useState(null);
  const [membersSearchTerm, setMembersSearchTerm] = useState('');

  /** @type {Object} Loading state for position expansions */
  const [expandingPositions, setExpandingPositions] = useState({});

  /** @type {Array} Goals for the selected user */
  const [userGoals, setUserGoals] = useState([]);

  /** @type {boolean} Loading state for goals data */
  const [goalsLoading, setGoalsLoading] = useState(false);

  /** @type {boolean} Loading state for goals progress data */
  const [goalsProgressLoading, setGoalsProgressLoading] = useState(false);

  /** @type {Object} Cache for progress data: { goalId: { date: progressData } } */
  const progressCacheRef = useRef({});

  /** @type {Object} Ref to store debounce timer */
  const debounceTimerRef = useRef(null);

  /** @type {AbortController} Ref to cancel previous API calls */
  const dailySummaryAbortControllerRef = useRef(null);
  const goalsProgressAbortControllerRef = useRef(null);

  /** @type {Object} Ref to cache member counts to prevent recalculation on every render */
  const memberCountsCacheRef = useRef({});
  
  /** @type {Object} Ref to track if calculations are in progress to prevent concurrent calls */
  const calculationInProgressRef = useRef({});

  /** @type {Array} Ref to track current goals for async operations */
  const userGoalsRef = useRef([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    userGoalsRef.current = userGoals;
  }, [userGoals]);

  /** @type {boolean} Show goal drawer */
  const [showGoalDrawer, setShowGoalDrawer] = useState(false);
  const [goalDrawerMode, setGoalDrawerMode] = useState(null); // 'add' or 'edit'
  const [editingGoal, setEditingGoal] = useState(null);

  /** @type {Object|null} HR metrics data for selected user */
  const [hrMetricsData, setHrMetricsData] = useState(null);

  /** @type {boolean} Loading state for HR metrics data */
  const [hrMetricsLoading, setHrMetricsLoading] = useState(false);

  /** @type {string} Active tab in work details panel ('overview' | 'goals') */
  const [workDetailsTab, setWorkDetailsTab] = useState("overview");

  /** @type {string|null} Currently expanded goal analysis ID */
  const [expandedGoalAnalysis, setExpandedGoalAnalysis] = useState(null);

  /** @type {Object} Form data for creating new goals */
  const [goalForm, setGoalForm] = useState({
    title: "",
    description: "",
    type: "",
    targetValue: "",
    unit: "",
    startDate: "",
    endDate: "",
    endTime: null, // TimePicker value (dayjs object)
  });

  /** @type {boolean} Loading state for goal creation */
  const [isCreatingGoal, setIsCreatingGoal] = useState(false);

  /** @type {boolean} Loading state for goal update */
  const [isUpdatingGoal, setIsUpdatingGoal] = useState(false);

  /** @type {string} Active tab in right panel ('work' | 'goals') */

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Builds hierarchical structure for display from organizational data
   * @param {Array} structure - Organizational structure data
   * @param {Array} usersData - Users data
   * @returns {Array} Processed hierarchy for display
   */
  const buildHierarchy = useCallback((structure, usersData) => {
    if (!structure || structure.length === 0) return [];

    // For org_user, filter levels based on their position
    if (user?.role === 'org_user' && user?.position) {
      // Find user's level
      let userLevelIndex = -1;
      for (let i = 0; i < structure.length; i++) {
        const level = structure[i];
        if (level.positions && level.positions.some(pos => pos.roleName.trim() === user.position.trim())) {
          userLevelIndex = i;
          break;
        }
      }
      
      // If user's position found, show from their level onwards
      if (userLevelIndex !== -1) {
        const filteredStructure = structure.slice(userLevelIndex);
        return filteredStructure.map((level, index) => {
          let filteredPositions = level.positions;
          
          // For the first level (user's level), show only their position
          if (index === 0) {
            filteredPositions = level.positions.filter(pos => 
              pos.roleName.trim() === user.position.trim()
            );
          }
          
          return {
            levelId: index + 1, // Start from 1
            levelName: level.levelName,
            positions: filteredPositions,
            users: {}, // Simplified structure - users are fetched dynamically
          };
        });
      }
    }

    // For main_admin and org_admin, show all levels as before
    return structure.map((level) => ({
      levelId: level.levelId,
      levelName: level.levelName,
      positions: level.positions,
      users: {}, // Simplified structure - users are fetched dynamically
    }));
  }, [user]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  /**
   * Fetches organizational data including team structure and users
   * Uses separate endpoints to avoid pagination issues
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // USERS_LIST_PERF: these two calls are independent — they used to run one
      // after the other, so the page paid both round trips end to end.
      //
      // /users/hierarchy-list replaces /api/v1/users?limit=1000, which was taking
      // 17-22s on a Screenshot aggregate whose result this page never reads.
      // The new endpoint is additive — /api/v1/users is unchanged — and keeps the
      // same org_user access filter, so nobody sees more people than before.
      // NOT /users/all: that one deliberately skips the position-based filter.
      const [structureResponse, usersResponse] = await Promise.all([
        api.get("/api/v1/organizations/team-structure"),
        api.get("/api/v1/users/hierarchy-list"),
      ]);

      const structure = structureResponse.data.success
        ? structureResponse.data.data.teamStructure
        : [];

      const usersData = usersResponse.data.success
        ? usersResponse.data.data.users
        : [];
      setUsers(usersData);

      // Build hierarchical structure for display
      const hierarchyData = buildHierarchy(structure, usersData);
      setHierarchy(hierarchyData);
      setFilteredHierarchy(hierarchyData); // Initialize filtered hierarchy
    } catch (error) {
      console.error("Error fetching organizational data:", error);
    } finally {
      setLoading(false);
    }
  }, [buildHierarchy]);

  /**
   * Fetches daily summary data for a specific user and date
   * @param {string} userId - User ID to fetch data for
   * @param {Date} date - Date to fetch data for
   * @param {AbortSignal} signal - AbortSignal to cancel the request
   */
  const fetchDailySummaryData = useCallback(async (userId, date, signal = null) => {
    if (!userId || !date) return null;

    try {
      setDailySummaryLoading(true);
      
      // Format date in local timezone (not UTC) to avoid date shifting
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      // Fetch both live analytics and daily summary data with abort signal
      const [liveAnalyticsResponse, dailySummaryResponse] =
        await Promise.allSettled([
          api.get(`/api/v1/live-analytics/user/${userId}`, {
            params: { date: dateString },
            signal: signal,
          }),
          api.get(`/api/v1/daily-summaries/user/${userId}/${dateString}`, {
            signal: signal,
          }),
        ]);

      // Check if request was aborted
      if (signal?.aborted) {
        console.log('🚫 Daily summary API call was cancelled');
        return null;
      }

      let combinedData = null;

      // Process live analytics data
      if (
        liveAnalyticsResponse.status === "fulfilled" &&
        liveAnalyticsResponse.value.data.success &&
        liveAnalyticsResponse.value.data.data.metrics
      ) {
        const metrics = liveAnalyticsResponse.value.data.data.metrics;
        combinedData = {
          totalSessionTimeMinutes: metrics.totalMinutes,
          totalScreenshots: metrics.totalScreenshots,
          totalHoursWorked: metrics.hoursWorked,
          activitySummary: {
            avgProductivityScore: metrics.avgProductivityScore,
            totalKeystrokes: metrics.totalKeystrokes,
            totalMouseActivity: metrics.totalMouseActivity,
            totalApplications: metrics.uniqueApps,
            totalUrls: metrics.uniqueUrls,
          },
          topApplications: metrics.topApplications,
          topUrls: metrics.topUrls,
        };
      }

      // Process daily summary data (AI analysis)
      if (
        dailySummaryResponse.status === "fulfilled" &&
        dailySummaryResponse.value.data.success &&
        dailySummaryResponse.value.data.data
      ) {
        const summaryData = dailySummaryResponse.value.data.data;

        if (combinedData) {
          // Merge with live analytics data
          combinedData.aiDailySummary = {
            summary: summaryData.summary,
            productivityAnalysis: summaryData.productivityAnalysis,
            workSummary: summaryData.workSummary,
            keyInsights: summaryData.keyInsights || [],
            recommendations: summaryData.recommendations || [],
          };
          combinedData.workTimeSlots = summaryData.workTimeSlots || [];
          // Extract goals progress from goalSummary.goals array
          combinedData.goalsProgress = summaryData.goalSummary?.goals || summaryData.goalsProgress || [];
          combinedData.goalSummary = summaryData.goalSummary || null;
        } else {
          // Use daily summary data alone if live analytics not available
          combinedData = {
            totalSessionTimeMinutes: summaryData.totalSessionTimeMinutes || 0,
            totalScreenshots: summaryData.totalScreenshots || 0,
            totalHoursWorked: summaryData.totalHoursWorked || 0,
            activitySummary: summaryData.activitySummary || {},
            topApplications: summaryData.topApplications || [],
            topUrls: summaryData.topUrls || [],
            workTimeSlots: summaryData.workTimeSlots || [],
            // Extract goals progress from goalSummary.goals array
            goalsProgress: summaryData.goalSummary?.goals || summaryData.goalsProgress || [],
            goalSummary: summaryData.goalSummary || null,
            aiDailySummary: {
              summary: summaryData.summary,
              productivityAnalysis: summaryData.productivityAnalysis,
              workSummary: summaryData.workSummary,
              keyInsights: summaryData.keyInsights || [],
              recommendations: summaryData.recommendations || [],
            },
          };
        }
      }

      setDailySummaryData(combinedData);
      
      // Return the combined data including goalsProgress for merging with goals
      return combinedData;
    } catch (error) {
      // Don't log error if request was aborted
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('🚫 Daily summary API call was cancelled');
        return null;
      }
      console.error("Error fetching daily summary data:", error);
      setDailySummaryData(null);
      return null;
    } finally {
      // Only set loading to false if request wasn't aborted
      if (!signal?.aborted) {
      setDailySummaryLoading(false);
    }
    }
  }, [api]);

  /**
   * Fetches goals list for a specific user (without progress)
   * @param {string} userId - User ID to fetch goals for
   */
  const fetchUserGoals = useCallback(async (userId) => {
    if (!userId) return;

    try {
      setGoalsLoading(true);

      // Fetch goals for the specific target user
      const response = await api.get(`/api/v1/goals/for-user/${userId}`, {
        params: {
          status: "active",
          limit: 50,
        },
      });

      if (response.data.success) {
        const goals = response.data.data.goals || [];
        
        // Preserve existing progress when updating goals list
        setUserGoals(prevGoals => {
          // Create a map of existing goals with their progress
          const progressMap = new Map();
          prevGoals.forEach(prevGoal => {
            if (prevGoal.progress) {
              progressMap.set(prevGoal._id, prevGoal.progress);
            }
          });
          
          // Merge new goals with existing progress
          const goalsWithPreservedProgress = goals.map(goal => {
            const existingProgress = progressMap.get(goal._id);
            return existingProgress ? { ...goal, progress: existingProgress } : goal;
          });
          
          console.log('📊 Goals list loaded with preserved progress:', {
            userId: userId,
            goalsCount: goals.length,
            goalsWithProgress: goalsWithPreservedProgress.filter(g => g.progress).length
          });
          
          return goalsWithPreservedProgress;
        });
      } else {
        console.warn("Goals API returned unsuccessful response:", response.data);
        setUserGoals([]);
      }
    } catch (error) {
      console.error("Error fetching user goals:", error);
      if (error.response?.data?.message) {
        console.error("API Error:", error.response.data.message);
      } else if (error.response?.status) {
        console.error("HTTP Error:", error.response.status, error.response.statusText);
      } else {
        console.error("Network Error:", error.message);
      }
      setUserGoals([]);
    } finally {
      setGoalsLoading(false);
    }
  }, [api]);

  /**
   * Fetches progress for goals that are active on the selected date
   * Uses daily-summaries API data if available, otherwise falls back to individual goal progress API
   * @param {Array} goals - Array of goal objects
   * @param {Date} date - Date to fetch progress for
   * @param {Object} dailySummaryData - Daily summary data containing goalsProgress (optional)
   * @param {AbortSignal} signal - AbortSignal to cancel the request
   * @param {string} userId - Owner of these goals; enables the single bulk
   *   progress request instead of one request per goal (GOALS_PROGRESS_PERF)
   * @returns {Array} Goals with progress data
   */
  const fetchGoalsProgress = useCallback(async (goals, date, dailySummaryData = null, signal = null, userId = null) => {
    if (!goals || goals.length === 0 || !date) return goals;

    try {
      setGoalsProgressLoading(true);

      // Format date in local timezone (YYYY-MM-DD)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      // Filter goals that are active on the selected date
      const activeGoals = goals.filter((goal) => {
        if (!goal.startDate || !goal.endDate) return true;
        
        const goalStartDate = goal.startDate.includes('T') ? goal.startDate.split('T')[0] : goal.startDate;
        const goalEndDate = goal.endDate.includes('T') ? goal.endDate.split('T')[0] : goal.endDate;
        
        return dateString >= goalStartDate && dateString <= goalEndDate;
      });

      if (activeGoals.length === 0) {
        // No active goals for this date, return goals without progress
        return goals.map(goal => ({ ...goal, progress: null }));
      }

      // First, try to use goalsProgress from daily-summaries API if available
      // Check both goalSummary.goals and goalsProgress for compatibility
      let goalsProgressFromDailySummary = null;
      if (dailySummaryData?.goalSummary?.goals && Array.isArray(dailySummaryData.goalSummary.goals)) {
        goalsProgressFromDailySummary = dailySummaryData.goalSummary.goals;
        console.log('📊 Using goal progress from daily-summaries API (goalSummary.goals):', goalsProgressFromDailySummary.length, 'goals');
      } else if (dailySummaryData?.goalsProgress && Array.isArray(dailySummaryData.goalsProgress)) {
        goalsProgressFromDailySummary = dailySummaryData.goalsProgress;
        console.log('📊 Using goal progress from daily-summaries API (goalsProgress):', goalsProgressFromDailySummary.length, 'goals');
      }

      // GOALS_PROGRESS_PERF: the PRIORITY 3 fallback below used to fire one
      // request per goal. It only ever runs when daily-summaries has no goal
      // progress at all, so when that is the case we resolve the whole set with
      // ONE bulk request up front and let each goal read its entry from the map.
      // Same data, same shape — /progress/goals/user/:userId returns per-goal
      // `progress` objects identical to the individual endpoint's `data.progress`.
      const needsFallback = !goalsProgressFromDailySummary || goalsProgressFromDailySummary.length === 0;
      let bulkProgressById = null;

      if (needsFallback && userId) {
        try {
          const bulkResponse = await api.get(`/api/v1/progress/goals/user/${userId}`, {
            params: { date: dateString },
            signal: signal,
          });

          if (bulkResponse.data?.success && Array.isArray(bulkResponse.data.data?.goals)) {
            bulkProgressById = new Map();
            bulkResponse.data.data.goals.forEach((g) => {
              const id = g.goalId ? String(g.goalId) : (g._id ? String(g._id) : null);
              if (id && g.progress) bulkProgressById.set(id, g.progress);
            });
            console.log('📡 Bulk progress loaded in ONE request:', {
              date: dateString,
              goalsReturned: bulkProgressById.size,
              requestsSaved: Math.max(0, activeGoals.length - 1)
            });
          }
        } catch (error) {
          // Aborted or failed — fall through. Each goal then keeps whatever
          // progress it already had, exactly as before.
          if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
            console.warn('Failed to fetch bulk goals progress:', error);
          }
        }

        if (signal?.aborted) {
          return goals.map(goal => ({ ...goal, progress: goal.progress || null }));
        }
      }

      // Fetch progress for active goals (with caching and daily-summaries data)
      // Priority: 1. Daily-summaries data (always use this if available), 2. Cache, 3. Bulk progress map
      // IMPORTANT: Never overwrite progress from daily-summaries, even if the bulk map has an entry
        const goalsWithProgress = await Promise.all(
        activeGoals.map(async (goal) => {
          const goalIdStr = goal._id ? String(goal._id) : null;
          let progressData = null;
          let isFromDailySummary = false; // Track if progress is from daily-summaries
          
          // PRIORITY 1: Try to find progress from daily-summaries API FIRST (highest priority)
          if (goalsProgressFromDailySummary && goalsProgressFromDailySummary.length > 0) {
            const dailySummaryProgress = goalsProgressFromDailySummary.find((gp) => {
              const gpGoalIdStr = gp.goalId ? String(gp.goalId) : null;
              return gpGoalIdStr === goalIdStr;
            });
            
            if (dailySummaryProgress) {
              // Use daily-summaries format directly (no conversion needed)
              progressData = dailySummaryProgress;
              isFromDailySummary = true; // Mark as from daily-summaries
              
              // Cache the progress data (store the entire dailySummaryProgress object)
              if (!progressCacheRef.current[goal._id]) {
                progressCacheRef.current[goal._id] = {};
              }
              progressCacheRef.current[goal._id][dateString] = dailySummaryProgress;
              
              console.log('✅ Using progress from daily-summaries for goal:', {
                goalTitle: goal.title,
                goalId: goal._id,
                completionPercentage: dailySummaryProgress.completionPercentage,
                dailyContribution: dailySummaryProgress.dailyContribution,
                isOnTrack: dailySummaryProgress.isOnTrack,
                needsAttention: dailySummaryProgress.needsAttention
              });
              
              return {
                ...goal,
                progress: dailySummaryProgress
              };
            } else {
              console.log('⚠️ Goal not found in daily-summaries:', {
                goalTitle: goal.title,
                goalId: goalIdStr,
                availableGoalIds: goalsProgressFromDailySummary.map(gp => String(gp.goalId))
              });
            }
          }

          // PRIORITY 2: Check cache only if daily-summaries doesn't have it
          // IMPORTANT: Check if cached data is from daily-summaries (don't overwrite it)
          if (!progressData && progressCacheRef.current[goal._id] && progressCacheRef.current[goal._id][dateString]) {
            const cachedProgress = progressCacheRef.current[goal._id][dateString];
            // Check if cached progress has daily-summaries structure (has goalId, dailyContribution, etc.)
            const isCachedFromDailySummary = cachedProgress && (
              cachedProgress.goalId || 
              cachedProgress.dailyContribution !== undefined ||
              (cachedProgress.completionPercentage !== undefined && !cachedProgress.currentValue)
            );
            
            if (isCachedFromDailySummary) {
              // Cached data is from daily-summaries, use it and don't overwrite
              console.log('💾 Using cached progress from daily-summaries for goal:', {
                goalTitle: goal.title,
                goalId: goal._id,
                completionPercentage: cachedProgress.completionPercentage
              });
              return {
                ...goal,
                progress: cachedProgress
              };
            } else {
              // Regular cached data, can be used
              progressData = cachedProgress;
              console.log('💾 Using cached progress for goal:', {
                goalTitle: goal.title,
                goalId: goal._id,
                completionPercentage: progressData.completionPercentage
              });
              return {
                ...goal,
                progress: progressData
              };
            }
          }

          // PRIORITY 3: Fallback to individual goal progress API only if daily-summaries and cache don't have it
          // IMPORTANT: If daily-summaries data is available, we should have all goals' progress from it
          // Only make individual API calls if daily-summaries data is completely unavailable
          // AND never overwrite if goal already has progress from daily-summaries
          if (!progressData && !isFromDailySummary) {
            // Check if daily-summaries data was available but this goal wasn't in it
            if (goalsProgressFromDailySummary && goalsProgressFromDailySummary.length > 0) {
              // Daily-summaries data exists, so this goal probably doesn't have progress for this date
              // Skip individual API call to avoid unnecessary requests
              console.log('⏭️ Skipping individual API call - daily-summaries data available (goal not in response, likely no progress for this date):', {
                goalTitle: goal.title,
                goalId: goal._id,
                dailySummaryGoalsCount: goalsProgressFromDailySummary.length
              });
            } else {
              // Daily-summaries data is not available, so make individual API call as fallback
              // BUT: Check if goal already has progress from daily-summaries before overwriting
              if (goal.progress && (goal.progress.goalId || goal.progress.dailyContribution !== undefined)) {
                // Goal already has progress from daily-summaries, don't overwrite
                console.log('🛡️ Preserving daily-summaries progress - skipping individual API call:', {
                  goalTitle: goal.title,
                  goalId: goal._id,
                  existingProgress: goal.progress.completionPercentage
                });
                return {
                  ...goal,
                  progress: goal.progress // Keep existing progress
                };
              }
              
              // GOALS_PROGRESS_PERF: read from the single bulk response fetched
              // above instead of issuing a request for this goal. No I/O here.
              const bulkProgress = bulkProgressById ? bulkProgressById.get(goalIdStr) : null;

              if (bulkProgress) {
                progressData = bulkProgress;

                // IMPORTANT: Don't overwrite if goal already has daily-summaries progress
                if (goal.progress && (goal.progress.goalId || goal.progress.dailyContribution !== undefined)) {
                  console.log('🛡️ Preserving daily-summaries progress - not overwriting with bulk progress:', {
                    goalTitle: goal.title,
                    goalId: goal._id,
                    dailySummaryProgress: goal.progress.completionPercentage,
                    bulkProgress: progressData.completionPercentage
                  });
                  return {
                    ...goal,
                    progress: goal.progress // Keep daily-summaries progress
                  };
                }

                // Cache the progress data (only if not from daily-summaries)
                if (!progressCacheRef.current[goal._id]) {
                  progressCacheRef.current[goal._id] = {};
                }
                progressCacheRef.current[goal._id][dateString] = progressData;

                console.log('📊 Using bulk progress for goal (daily-summaries not available):', {
                  goalTitle: goal.title,
                  goalId: goal._id,
                  completionPercentage: progressData.completionPercentage
                });

                return {
                  ...goal,
                  progress: progressData
                };
              }
            }
          }
          
          // Return goal with existing progress if available, otherwise without progress
          return { ...goal, progress: goal.progress || null };
        })
      );

      // Merge with goals that are not active on this date
      const inactiveGoals = goals.filter((goal) => {
        if (!goal.startDate || !goal.endDate) return false;
        const goalStartDate = goal.startDate.includes('T') ? goal.startDate.split('T')[0] : goal.startDate;
        const goalEndDate = goal.endDate.includes('T') ? goal.endDate.split('T')[0] : goal.endDate;
        return !(dateString >= goalStartDate && dateString <= goalEndDate);
      }).map(goal => ({ ...goal, progress: null }));

      const allGoals = [...goalsWithProgress, ...inactiveGoals];
      
      console.log('📊 Goals progress loaded:', {
        date: dateString,
        activeGoalsCount: activeGoals.length,
        goalsWithProgressCount: goalsWithProgress.filter(g => g.progress).length
      });

      return allGoals;
    } catch (error) {
      console.error("Error fetching goals progress:", error);
      return goals.map(goal => ({ ...goal, progress: null }));
    } finally {
      setGoalsProgressLoading(false);
    }
  }, [api]);

  /**
   * Fetch user goals with progress data
   * @param {string} userId - User ID to fetch goals for
   */
  const fetchUserGoalsWithProgress = useCallback(async (userId) => {
    if (!userId) return;

    try {
      setGoalsLoading(true);

      // Fetch goals with progress for the specific target user
      const response = await api.get(`/api/v1/progress/goals/user/${userId}`, {
        params: {
          date: new Date().toISOString().split('T')[0], // Today's date
        },
      });

      if (response.data.success) {
        setUserGoals(response.data.data.goals || []);
        console.log('📊 Goals with progress loaded:', {
          userId: userId,
          goalsCount: response.data.data.goals?.length || 0,
          lastCalculated: response.data.data.lastCalculated
        });
      } else {
        console.warn("Progress API returned unsuccessful response:", response.data);
        // Fallback to regular goals fetch
        await fetchUserGoals(userId);
      }
    } catch (error) {
      console.error("Error fetching user goals with progress:", error);
      // Fallback to regular goals fetch
      await fetchUserGoals(userId);
    } finally {
      setGoalsLoading(false);
    }
  }, [fetchUserGoals]);

  /**
   * Fetches HR metrics data for a specific user
   * @param {string} userId - User ID to fetch HR metrics for
   */
  const fetchHRMetricsData = useCallback(async (userId) => {
    if (!userId) return;

    try {
      setHrMetricsLoading(true);

      // Calculate date range (last 30 days)
      const endDate = new Date();
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      const response = await api.get(`/api/v1/hr-metrics/${userId}`, {
        params: {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
        },
      });

      if (response.data.success) {
        setHrMetricsData(response.data.data.metrics);
      } else {
        setHrMetricsData(null);
      }
    } catch (error) {
      console.error("Error fetching HR metrics data:", error);
      if (error.response?.data?.message) {
        console.error("API Error:", error.response.data.message);
      }
      setHrMetricsData(null);
    } finally {
      setHrMetricsLoading(false);
    }
  }, []);

  /**
   * Creates a new goal for the selected user
   * @param {Object} goalData - Goal data to create
   */
  const createGoal = useCallback(
    async (goalData) => {
      try {
        const response = await api.post("/api/v1/goals", {
          ...goalData,
          targetUserId: workDetailsMember?._id,
        });

        if (response.data.success) {
          // Refresh goals list
          await fetchUserGoals(workDetailsMember?._id);
          setShowGoalDrawer(false);
          setGoalDrawerMode(null);
          // Show success message
          toast.success("Goal created successfully!");
        }
      } catch (error) {
        console.error("Error creating goal:", error);
        // Show error message
        const errorMessage = error.response?.data?.message || error.message || "Failed to create goal";
        toast.error(errorMessage);
      }
    },
    [workDetailsMember, fetchUserGoals]
  );

  /**
   * Deletes a goal
   * @param {string} goalId - Goal ID to delete
   */
  const deleteGoal = useCallback(
    async (goalId) => {
      try {
        const response = await api.delete(`/api/v1/goals/${goalId}`);

        if (response.data.success) {
          // Refresh goals list
          await fetchUserGoals(workDetailsMember?._id);
          toast.success("Goal deleted successfully!");
        }
      } catch (error) {
        console.error("Error deleting goal:", error);
        // Show error message
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete goal";
        toast.error(errorMessage);
      }
    },
    [workDetailsMember, fetchUserGoals]
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Initialize data fetching on component mount
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clear member counts cache and calculation flags when users array changes
  useEffect(() => {
    memberCountsCacheRef.current = {};
    calculationInProgressRef.current = {};
  }, [users.length]);

  // Fetch daily summary data when workDetailsMember or selectedDate changes (with debouncing and cancellation)
  useEffect(() => {
    if (!workDetailsMember || !selectedDate) return;

    // Cancel previous API call if it exists
    if (dailySummaryAbortControllerRef.current) {
      dailySummaryAbortControllerRef.current.abort();
    }

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    dailySummaryAbortControllerRef.current = abortController;

    // Store stable references to prevent stale closures and race conditions
    const currentMemberId = workDetailsMember._id;
    const currentDate = selectedDate;

    // Debounce the API call by 300ms
    debounceTimerRef.current = setTimeout(async () => {
      // Check if request was already aborted
      if (abortController.signal.aborted) {
        return;
      }

      // Verify member/date haven't changed (prevent stale updates from rapid clicks)
      if (!workDetailsMember || workDetailsMember._id !== currentMemberId || selectedDate !== currentDate) {
        return;
      }

      try {
        const dailySummaryData = await fetchDailySummaryData(
          currentMemberId, 
          currentDate, 
          abortController.signal
        );

        // If request was aborted during fetch or member/date changed, don't update state
        if (abortController.signal.aborted || !workDetailsMember || workDetailsMember._id !== currentMemberId || selectedDate !== currentDate) {
          return;
        }

        // Update goals progress with daily-summaries data if available
        // Use ref to get current goals to avoid dependency issues
        const currentGoals = userGoalsRef.current;
        if (currentGoals.length > 0) {
          const goalsWithProgress = await fetchGoalsProgress(
            currentGoals, 
            currentDate, 
            dailySummaryData,
            abortController.signal,
            currentMemberId // GOALS_PROGRESS_PERF: enables the single bulk request
          );
          
          // Only update if request wasn't aborted and member/date still match
          if (!abortController.signal.aborted && workDetailsMember && workDetailsMember._id === currentMemberId && selectedDate === currentDate) {
            // Always use the progress from daily-summaries (it's the source of truth)
            // But preserve progress for goals that weren't in daily-summaries response
            setUserGoals(prevGoals => {
              const progressMap = new Map();
              prevGoals.forEach(prevGoal => {
                if (prevGoal.progress) {
                  progressMap.set(prevGoal._id, prevGoal.progress);
                }
              });
              
              // Use new progress from daily-summaries if available, otherwise keep existing
              return goalsWithProgress.map(goal => {
                // If goal has progress from daily-summaries, use it (it's the source of truth)
                if (goal.progress) {
                  return goal;
                }
                // Otherwise, preserve existing progress if available
                const existingProgress = progressMap.get(goal._id);
                return existingProgress ? { ...goal, progress: existingProgress } : goal;
              });
            });
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error in daily summary fetch:', error);
        }
      }
    }, 300);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (dailySummaryAbortControllerRef.current) {
        dailySummaryAbortControllerRef.current.abort();
      }
    };
  }, [workDetailsMember?._id, selectedDate, fetchDailySummaryData, fetchGoalsProgress]);

  // Fetch goals list when workDetailsMember changes (only goals, not progress)
  useEffect(() => {
    if (workDetailsMember) {
      fetchUserGoals(workDetailsMember._id);
      // Clear progress cache when member changes
      progressCacheRef.current = {};
    }
  }, [workDetailsMember, fetchUserGoals]);

  // NOTE: Removed initial progress fetch useEffect
  // The daily-summaries useEffect (line 946) already handles fetching progress when date/user changes
  // This prevents unnecessary individual API calls - daily-summaries API provides all goal progress in one call

  // Fetch HR metrics when workDetailsMember changes
  useEffect(() => {
    if (workDetailsMember) {
      fetchHRMetricsData(workDetailsMember._id);
    }
  }, [workDetailsMember, fetchHRMetricsData]);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Gets the count of positions in a level
   * @param {Object} level - Level object from hierarchy
   * @returns {number} Number of positions in the level
   */
  const getLevelPositionCount = useCallback((level) => {
    return level.positions ? level.positions.length : 0;
  }, []);

  /**
   * Gets all members for a specific position
   * @param {string} positionName - Name of the position
   * @returns {Array} Array of users with the specified position
   */

  /**
   * Calendar helper functions
   */
  const getDaysInMonth = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }, []);

  const getFirstDayOfMonth = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  }, []);

  const formatDate = useCallback((date) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  }, []);

  const isSameDay = useCallback((date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }, []);

  const isToday = useCallback(
    (date) => {
      const today = new Date();
      return isSameDay(date, today);
    },
    [isSameDay]
  );
  const getMembersForPosition = useCallback(
    (positionName) => {
      // Trim both positionName and user.position to handle trailing spaces
      const trimmedPositionName = positionName?.trim() || '';
      return users.filter((user) => {
        const trimmedUserPosition = user.position?.trim() || '';
        return trimmedUserPosition === trimmedPositionName;
      });
    },
    [users]
  );

  /**
   * Helper function to normalize manager ID for comparison
   * @param {string|Object} managerId - Manager ID (string or ObjectId)
   * @returns {string} Normalized manager ID string
   */
  const normalizeManagerId = useCallback((managerId) => {
    return typeof managerId === "object"
      ? managerId._id || managerId.toString()
      : managerId;
  }, []);

  /**
   * Helper function to check if a user reports to a manager (via reportingManager or reportingManagers array)
   * @param {Object} user - User object to check
   * @param {string} managerId - Manager ID to check against
   * @returns {boolean} True if user reports to manager
   */
  const reportsToManager = useCallback((user, managerId) => {
    // Check reportingManager (single field)
    if (user.reportingManager) {
      if (normalizeManagerId(user.reportingManager) === managerId) {
        return true;
      }
    }
    
    // Check reportingManagers array
    if (user.reportingManagers && Array.isArray(user.reportingManagers) && user.reportingManagers.length > 0) {
      return user.reportingManagers.some(manager => {
        return normalizeManagerId(manager) === managerId;
      });
    }
    
    return false;
  }, [normalizeManagerId]);

  /**
   * Gets the count of direct reports for a user
   * @param {string} userId - User ID to check
   * @returns {number} Count of direct reports
   */
  const getDirectReportsCount = useCallback(
    (userId) => {
      return users.filter((user) => {
        return reportsToManager(user, userId);
      }).length;
    },
    [users, reportsToManager]
  );

  /**
   * Gets the count of indirect reports for a user (recursive calculation)
   * @param {string} userId - User ID to check
   * @param {Set} visited - Set to track visited users and prevent circular references
   * @returns {number} Count of indirect reports
   */
  const getIndirectReportsCount = useCallback(
    (userId, visited = new Set(), depth = 0) => {
      // Normalize userId to string for consistent comparison
      const userIdStr = String(userId);
      
      // Check depth limit FIRST to prevent any work if too deep (reduced to 10 for safety)
      if (depth >= 10) {
        console.warn('Maximum depth reached in getIndirectReportsCount for user:', userId, 'depth:', depth);
        return 0;
      }
      
      // Prevent circular references - check BEFORE adding
      if (visited.has(userIdStr)) {
        return 0;
      }
      
      // Add current user to visited set BEFORE processing
      visited.add(userIdStr);
      
      try {
        const directReports = users.filter((user) => {
          return reportsToManager(user, userId);
        });

        let indirectCount = 0;

        // Count reports of direct reports (true indirect reports)
        directReports.forEach((report) => {
          if (report && report._id) {
            indirectCount += getDirectReportsCount(report._id);
            // Pass visited set and increment depth to prevent circular references and stack overflow
            indirectCount += getIndirectReportsCount(report._id, visited, depth + 1);
          }
        });

        return indirectCount;
      } catch (error) {
        console.error('Error in getIndirectReportsCount:', error);
        return 0;
      }
    },
    [users, reportsToManager, getDirectReportsCount]
  );

  /**
   * Gets the list of direct reports for a user
   * @param {string} userId - User ID to check
   * @returns {Array} Array of direct report users
   */
  const getDirectReports = useCallback(
    (userId) => {
      return users.filter((user) => {
        return reportsToManager(user, userId);
      });
    },
    [users, reportsToManager]
  );

  /**
   * Gets the list of indirect reports for a user (recursive)
   * @param {string} userId - User ID to check
   * @param {Set} visited - Set to track visited users and prevent circular references
   * @param {number} depth - Current recursion depth
   * @returns {Array} Array of indirect report users
   */
  const getIndirectReports = useCallback(
    (userId, visited = new Set(), depth = 0) => {
      // Normalize userId to string for consistent comparison
      const userIdStr = String(userId);
      
      // Check depth limit FIRST to prevent any work if too deep (reduced to 10 for safety)
      if (depth >= 10) {
        console.warn('Maximum depth reached in getIndirectReports for user:', userId, 'depth:', depth);
        return [];
      }
      
      // Prevent circular references - check BEFORE adding
      if (visited.has(userIdStr)) {
        return [];
      }
      
      // Add current user to visited set BEFORE processing
      visited.add(userIdStr);
      
      try {
        const directReports = getDirectReports(userId);
        let allIndirectReports = [];

        directReports.forEach((report) => {
          if (report && report._id) {
            // Get the indirect reports of this direct report (reports of reports)
            const indirectOfDirect = getDirectReports(report._id);
            allIndirectReports = allIndirectReports.concat(indirectOfDirect);
            // Recursively get further indirect reports with visited set and depth
            allIndirectReports = allIndirectReports.concat(
              getIndirectReports(report._id, visited, depth + 1)
            );
          }
        });

        return allIndirectReports;
      } catch (error) {
        console.error('Error in getIndirectReports:', error);
        return [];
      }
    },
    [getDirectReports]
  );

  // Memoize counts for selected member to prevent unnecessary recalculations during tab switches
  // Use cache key based on member ID and users array length to invalidate when users change
  const selectedMemberDirectCount = useMemo(() => {
    if (!selectedMember?._id) return 0;
    const cacheKey = `${selectedMember._id}-${users.length}`;
    const cacheKeyFull = `direct-${cacheKey}`;
    
    // Check cache first - return immediately if cached
    if (memberCountsCacheRef.current[cacheKeyFull] !== undefined) {
      return memberCountsCacheRef.current[cacheKeyFull];
    }
    
    // Calculate and cache
    try {
      const count = getDirectReportsCount(selectedMember._id);
      memberCountsCacheRef.current[cacheKeyFull] = count;
      return count;
    } catch (error) {
      console.error('Error calculating direct reports count:', error);
      // Cache the error result to prevent repeated failures
      memberCountsCacheRef.current[cacheKeyFull] = 0;
      return 0;
    }
  }, [selectedMember?._id, users.length, getDirectReportsCount]);

  const selectedMemberIndirectCount = useMemo(() => {
    if (!selectedMember?._id) return 0;
    const cacheKey = `${selectedMember._id}-${users.length}`;
    const cacheKeyFull = `indirect-${cacheKey}`;
    
    // Check cache first - return immediately if cached
    if (memberCountsCacheRef.current[cacheKeyFull] !== undefined) {
      return memberCountsCacheRef.current[cacheKeyFull];
    }
    
    // Prevent concurrent calculations
    if (calculationInProgressRef.current[cacheKeyFull]) {
      console.warn('Calculation already in progress for:', cacheKeyFull);
      return 0; // Return 0 temporarily to prevent stack overflow
    }
    
    // Mark calculation as in progress
    calculationInProgressRef.current[cacheKeyFull] = true;
    
    // Calculate and cache with error handling
    try {
      const count = getIndirectReportsCount(selectedMember._id);
      memberCountsCacheRef.current[cacheKeyFull] = count;
      calculationInProgressRef.current[cacheKeyFull] = false;
      return count;
    } catch (error) {
      console.error('Error calculating indirect reports count:', error);
      // Cache the error result to prevent repeated failures and stack overflow
      memberCountsCacheRef.current[cacheKeyFull] = 0;
      calculationInProgressRef.current[cacheKeyFull] = false;
      return 0;
    }
  }, [selectedMember?._id, users.length, getIndirectReportsCount]);

  /**
   * Gets the organizational level for a position
   * @param {string} positionName - Position name to check
   * @returns {string} Level ID or '?' if not found
   */
  const getUserLevel = useCallback(
    (positionName) => {
      for (let i = 0; i < hierarchy.length; i++) {
        const level = hierarchy[i];
        if (level.positions.some((pos) => pos.roleName === positionName)) {
          return level.levelId;
        }
      }
      return "?";
    },
    [hierarchy]
  );

  // ============================================================================
  // SEARCH HELPERS
  // ============================================================================

  /**
   * Highlights search term in text
   * @param {string} text - Text to highlight
   * @param {string} searchTerm - Term to highlight
   * @returns {JSX.Element} Highlighted text
   */
  const highlightSearchTerm = useCallback((text, searchTerm) => {
    if (!searchTerm) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          className="bg-yellow-500/30 text-yellow-300 px-1 rounded"
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  }, []);

  // ============================================================================
  // GOAL ANALYSIS HELPERS
  // ============================================================================

  /**
   * Toggles goal analysis expansion
   * @param {string} goalId - Goal ID to toggle analysis for
   */
  const toggleGoalAnalysis = useCallback((goalId) => {
    console.log('🔍 Toggle Goal Analysis clicked for goal:', goalId);
    setExpandedGoalAnalysis((prev) => {
      const newValue = prev === goalId ? null : goalId;
      console.log('🔍 Expanded Goal Analysis changed from', prev, 'to', newValue);
      return newValue;
    });
  }, []);


  // Helper component for goal analysis display
  const GoalAnalysisDisplay = ({ goal }) => {
    console.log('🔍 GoalAnalysisDisplay component rendered for goal:', goal._id, 'isCreatingGoal:', isCreatingGoal);
    
    const [analysis, setAnalysis] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      console.log('🔍 GoalAnalysisDisplay useEffect triggered for goal:', goal._id, 'isCreatingGoal:', isCreatingGoal);
      
      // Only load analysis if goal has a valid ID and is not a temporary goal
      if (!goal || !goal._id || goal._id.toString().length < 24) {
        console.log('⚠️ GoalAnalysisDisplay: Skipping analysis for invalid goal:', goal);
        setIsLoading(false);
        return;
      }

      // Additional check: only load if goal is not being created
      if (isCreatingGoal) {
        console.log('⚠️ GoalAnalysisDisplay: Skipping analysis during goal creation');
        setIsLoading(false);
        return;
      }

      const loadAnalysis = async () => {
        console.log('🔍 GoalAnalysisDisplay: Starting analysis for goal:', goal._id);
        setIsLoading(true);
        try {
          const analysisData = await getGoalAnalysis(goal);
          console.log('🔍 GoalAnalysisDisplay: Analysis completed:', analysisData);
          setAnalysis(analysisData);
        } catch (error) {
          console.error('❌ GoalAnalysisDisplay: Error loading analysis:', error);
          setAnalysis(null);
        } finally {
          setIsLoading(false);
        }
      };

      loadAnalysis();
    }, [goal._id]);
    
    // Early return if goal is being created or add goal modal is open
    if (isCreatingGoal || showGoalDrawer) {
      console.log('⚠️ GoalAnalysisDisplay: Component not rendered during goal creation or when add goal modal is open');
      return null;
    }

    if (isLoading) {
      return (
        <div className="p-4 text-center">
          <div className="inline-flex items-center gap-2 text-blue-400">
            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm">🤖 Generating AI Analysis...</span>
          </div>
        </div>
      );
    }

    if (!analysis) {
      return (
        <div className="p-4 text-center text-gray-400">
          <span className="text-sm">Analysis unavailable</span>
        </div>
      );
    }

    return (
      <>
        {/* Performance Status Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${
                analysis.trend === "trending-up"
                  ? "bg-green-400"
                  : analysis.trend === "trending-stable"
                  ? "bg-yellow-400"
                  : "bg-red-400"
              }`}
            ></div>
            <span className="text-sm font-medium text-white">
              Performance: {analysis.performanceStatus}
            </span>
          </div>
          <div
            className={`px-2 py-1 rounded text-xs font-medium ${
              analysis.trend === "trending-up"
                ? "bg-green-500/20 text-green-300"
                : analysis.trend === "trending-stable"
                ? "bg-yellow-500/20 text-yellow-300"
                : "bg-red-500/20 text-red-300"
            }`}
          >
            {analysis.trend === "trending-up"
              ? "↗ Trending Up"
              : analysis.trend === "trending-stable"
              ? "→ Stable"
              : "↘ Needs Attention"}
          </div>
        </div>

        {/* Key Insights */}
        <div className="mb-4">
          <h6 className="text-xs font-medium text-white mb-2">Key Insights</h6>
          <ul className="space-y-1">
            {analysis.insights.map((insight, index) => (
              <li key={index} className="flex items-start text-xs">
                <span className="text-blue-400 mr-2 mt-1">•</span>
                <span className="text-gray-300">{insight}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Enhanced AI Analysis Data */}
        {analysis.priorityLevel && (
          <div className="mb-4">
            <h6 className="text-xs font-medium text-white mb-2">
              Priority & Risk Assessment
            </h6>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-xs text-gray-400 mb-1">Priority Level</div>
                <div className={`text-sm font-medium ${
                  analysis.priorityLevel === 'High' ? 'text-red-400' :
                  analysis.priorityLevel === 'Medium' ? 'text-yellow-400' :
                  'text-green-400'
                }`}>
                  {analysis.priorityLevel}
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-xs text-gray-400 mb-1">Estimated Completion</div>
                <div className="text-sm font-medium text-blue-400">
                  {analysis.estimatedCompletion}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Factors */}
        {analysis.riskFactors && analysis.riskFactors.length > 0 && (
          <div className="mb-4">
            <h6 className="text-xs font-medium text-white mb-2">
              ⚠️ Risk Factors
            </h6>
            <div className="space-y-1">
              {analysis.riskFactors.map((risk, index) => (
                <div key={index} className="flex items-start text-xs">
                  <span className="text-red-400 mr-2 mt-1">⚠</span>
                  <span className="text-red-300">{risk}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Opportunities */}
        {analysis.opportunities && analysis.opportunities.length > 0 && (
          <div className="mb-4">
            <h6 className="text-xs font-medium text-white mb-2">
              🚀 Opportunities
            </h6>
            <div className="space-y-1">
              {analysis.opportunities.map((opportunity, index) => (
                <div key={index} className="flex items-start text-xs">
                  <span className="text-green-400 mr-2 mt-1">🚀</span>
                  <span className="text-green-300">{opportunity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Recommendations */}
        <div>
          <h6 className="text-sm font-medium text-white mb-2">
            💡 AI Recommendations
          </h6>
          <ul className="space-y-1 text-xs text-gray-300">
            {analysis.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-purple-400 mt-1.5 flex-shrink-0"></div>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  };

  /**
   * Fetches AI analysis for a goal from backend
   * @param {Object} goal - Goal object to analyze
   * @returns {Object} Analysis data
   */
  const fetchGoalAnalysis = useCallback(async (goal) => {
    try {
      // Prevent API call for invalid or temporary goals
      if (!goal || !goal._id || goal._id.toString().length < 24) {
        console.log('⚠️ Skipping analysis for invalid goal:', goal);
        return null;
      }

      // Prevent API call during goal creation
      if (isCreatingGoal) {
        console.log('⚠️ Skipping analysis during goal creation');
        return null;
      }

      console.log('🤖 Fetching AI analysis for goal:', goal.title);

      const response = await api.post(`/api/v1/goals/${goal._id}/analyze`);
      
      if (response.data.success) {
        const analysis = response.data.data.analysis;

        console.log('🤖 AI Goal Analysis Generated:', {
          goalTitle: goal.title,
          analysis: analysis
        });

        return analysis;
      }
    } catch (error) {
      console.error('Error fetching AI goal analysis:', error);
    }

    // Fallback to basic analysis if AI fails
    return generateFallbackGoalAnalysis(goal);
  }, [isCreatingGoal]);

  /**
   * Generates fallback analysis data (used when AI is unavailable)
   * @param {Object} goal - Goal object to analyze
   * @returns {Object} Analysis data
   */
  const generateFallbackGoalAnalysis = useCallback((goal) => {
    const progressPercentage = goal.progress?.completionPercentage || 0;

    // Generate realistic analysis based on goal data
    const analysis = {
      performanceStatus:
        progressPercentage >= 80
          ? "Excellent"
          : progressPercentage >= 60
          ? "Good"
          : progressPercentage >= 40
          ? "Needs Improvement"
          : "Critical",
      insights: [
        `Current progress: ${progressPercentage}% towards ${goal.targetValue} ${goal.unit} target`,
        progressPercentage >= 80
          ? "Team member is exceeding expectations"
          : progressPercentage >= 60
          ? "Team member is on track to meet goal"
          : progressPercentage >= 40
          ? "Additional support may be needed"
          : "Immediate intervention recommended",
        `Goal type: ${goal.type} - ${
          goal.type === "productivity"
            ? "Focus on efficiency metrics"
            : goal.type === "time"
            ? "Monitor time allocation"
            : goal.type === "application"
            ? "Track application usage patterns"
            : "Custom goal tracking required"
        }`,
      ],
      recommendations:
        progressPercentage >= 80
          ? [
              "Continue current approach",
              "Consider sharing best practices with team",
              "Set stretch goals for continued growth",
            ]
          : progressPercentage >= 60
          ? [
              "Maintain current momentum",
              "Monitor progress weekly",
              "Provide additional resources if needed",
            ]
          : [
              "Schedule one-on-one meeting",
              "Identify specific blockers",
              "Adjust goal timeline if necessary",
              "Provide additional training or support",
            ],
      trend:
        progressPercentage >= 70
          ? "trending-up"
          : progressPercentage >= 40
          ? "trending-stable"
          : "trending-down",
      priorityLevel: progressPercentage < 40 ? "High" : progressPercentage < 70 ? "Medium" : "Low",
      estimatedCompletion: `${Math.min(progressPercentage + 20, 100)}%`,
      riskFactors: progressPercentage < 40 ? [
        "Low progress indicates potential blockers",
        "Timeline may be at risk"
      ] : [],
      opportunities: progressPercentage >= 60 ? [
        "Consider setting stretch goals",
        "Share best practices with team"
      ] : []
    };

    return analysis;
  }, []);

  /**
   * Gets goal analysis (AI or fallback)
   * @param {Object} goal - Goal object to analyze
   * @returns {Object} Analysis data
   */
  const getGoalAnalysis = useCallback(async (goal) => {
    // Always fetch fresh AI analysis
    const analysis = await fetchGoalAnalysis(goal);
    return analysis || generateFallbackGoalAnalysis(goal);
  }, [fetchGoalAnalysis, generateFallbackGoalAnalysis]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles member click to open slide-in panel with team details
   * @param {Object} member - Member object to display details for
   */
  const handleMemberClick = useCallback((member) => {
    setSelectedMember(member);
    setWorkDetailsMember(member); // Set work details to the same member by default
    setShowPanel(true);
  }, []);

  /**
   * Handles "View Work" button click to open work details in panel
   * @param {Object} member - Member object to display work details for
   */
  const handleViewWork = useCallback((member) => {
    setWorkDetailsMember(member);
    setShowPanel(true);
  }, []);

  /**
   * Handles opening the add goal drawer
   */
  const handleOpenAddGoalModal = useCallback(() => {
    // Close any expanded analysis when opening add goal drawer
    setExpandedGoalAnalysis(null);
    // Reset form to empty values
    setGoalForm({
      title: "",
      description: "",
      type: "",
      targetValue: "",
      unit: "",
      startDate: "",
      endDate: "",
      endTime: null,
    });
    setGoalDrawerMode('add');
    setShowGoalDrawer(true);
  }, []);

  /**
   * Handles adding a new goal
   * @param {Object} values - Form values (from Ant Design Form onFinish)
   */
  const handleAddGoal = useCallback(
    async (values) => {
      if (!workDetailsMember) return;

      // Validate dates
      if (goalForm.startDate && goalForm.endDate) {
        const startDate = new Date(goalForm.startDate);
        const endDate = new Date(goalForm.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        
        // Check if start date is in the past
        if (startDate < today) {
          toast.error("Start date cannot be in the past");
          return;
        }
        
        // Check if end date is in the past
        if (endDate < today) {
          toast.error("End date cannot be in the past");
          return;
        }
        
        if (endDate < startDate) {
          toast.error("End date cannot be before start date");
          return;
        }
      }

      try {
        setIsCreatingGoal(true);
        // Close any expanded analysis when starting goal creation
        setExpandedGoalAnalysis(null);
        
        // Convert endTime from dayjs to 12-hour format string (e.g., "2:00 PM")
        let endTimeString = null;
        if (goalForm.endTime) {
          const hour = goalForm.endTime.hour();
          const minute = goalForm.endTime.minute();
          const isPM = hour >= 12;
          const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
          endTimeString = `${displayHour}:${minute.toString().padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;
        }
        
        const goalData = {
          targetUserId: workDetailsMember._id,
          title: goalForm.title,
          description: goalForm.description,
          type: goalForm.type,
          targetValue: parseFloat(goalForm.targetValue),
          unit: goalForm.unit,
          startDate: goalForm.startDate || new Date().toISOString().split('T')[0],
          endDate: goalForm.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          endTime: endTimeString,
        };

        console.log('🎯 Creating Goal with Data:', {
          title: goalData.title,
          startDate: goalData.startDate,
          endDate: goalData.endDate,
          formStartDate: goalForm.startDate,
          formEndDate: goalForm.endDate
        });

        const response = await api.post("/api/v1/goals", goalData);

        if (response.data.success) {
          toast.success("Goal created successfully");
          
          // Reset form
          setGoalForm({
            title: "",
            description: "",
            type: "",
            targetValue: "",
            unit: "",
            startDate: "",
            endDate: "",
            endTime: null,
          });

          // Refresh goals list and progress
          await fetchUserGoals(workDetailsMember._id);
          // Progress will be fetched automatically by useEffect when userGoals updates

          // Close modal
          setShowGoalDrawer(false);
          setGoalDrawerMode(null);

          console.log("Goal created successfully");
        }
      } catch (error) {
        console.error("Error creating goal:", error);
        // Extract error message from API response
        const errorMessage = 
          error.response?.data?.error?.message || 
          error.response?.data?.message || 
          error.message || 
          "Failed to create goal";
        toast.error(errorMessage);
      } finally {
        setIsCreatingGoal(false);
      }
    },
    [workDetailsMember, goalForm, fetchUserGoals]
  );

  // Delete goal function with mocked API
  const handleDeleteGoal = useCallback(
    async (goalId) => {
      Modal.confirm({
        title: (
          <span style={{ color: isDark ? '#ffffff' : '#000000', fontSize: '18px', fontWeight: 600 }}>
            Delete Goal
          </span>
        ),
        content: (
          <div style={{ color: isDark ? '#ffffff' : '#374151', fontSize: '14px', lineHeight: '1.6' }}>
            Are you sure you want to delete this goal? This action cannot be undone.
          </div>
        ),
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        styles: {
          content: {
            background: isDark ? '#000000' : '#ffffff',
          },
          header: {
            background: isDark ? '#000000' : '#ffffff',
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
          },
          body: {
            background: isDark ? '#000000' : '#ffffff',
          },
          footer: {
            background: isDark ? '#000000' : '#ffffff',
            borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
          },
        },
        onOk: async () => {
      try {
        const response = await api.delete(`/api/v1/goals/${goalId}`);

        if (response.data.success) {
              toast.success("Goal deleted successfully");
          
          // Optimistic update: Remove goal from state immediately
          setUserGoals(prevGoals => prevGoals.filter(goal => goal._id !== goalId));
          
          // Clear cache for deleted goal
          if (progressCacheRef.current[goalId]) {
            delete progressCacheRef.current[goalId];
          }
          
          // Refresh goals list to ensure consistency
          if (workDetailsMember) {
            await fetchUserGoals(workDetailsMember._id);
            // Progress will be fetched automatically by useEffect when userGoals updates
          }
          
          console.log("Goal deleted successfully");
        }
      } catch (error) {
        console.error("Error deleting goal:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to delete goal";
            toast.error(errorMessage);
          }
        },
      });
    },
    [workDetailsMember, fetchUserGoals, api, isDark]
  );

  // Edit goal function with mocked API
  const handleEditGoal = useCallback(
    async (values) => {
      if (!editingGoal) return;

      // Validate dates
      if (goalForm.startDate && goalForm.endDate) {
        const startDate = new Date(goalForm.startDate);
        const endDate = new Date(goalForm.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        
        // Check if start date is in the past
        if (startDate < today) {
          toast.error("Start date cannot be in the past");
          return;
        }
        
        // Check if end date is in the past
        if (endDate < today) {
          toast.error("End date cannot be in the past");
          return;
        }
        
        if (endDate < startDate) {
          toast.error("End date cannot be before start date");
          return;
        }
      }

      try {
        setIsUpdatingGoal(true);
        setExpandedGoalAnalysis(null);
        
        // Convert endTime from dayjs to 12-hour format string (e.g., "2:00 PM")
        let endTimeString = null;
        if (goalForm.endTime) {
          const hour = goalForm.endTime.hour();
          const minute = goalForm.endTime.minute();
          const isPM = hour >= 12;
          const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
          endTimeString = `${displayHour}:${minute.toString().padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;
        }

        const goalData = {
          title: goalForm.title,
          description: goalForm.description,
          type: goalForm.type,
          targetValue: parseFloat(goalForm.targetValue),
          unit: goalForm.unit,
          startDate: goalForm.startDate,
          endDate: goalForm.endDate,
          endTime: endTimeString,
        };

        console.log('✏️ Updating Goal with Data:', {
          goalId: editingGoal._id,
          ...goalData
        });

        const response = await api.put(`/api/v1/goals/${editingGoal._id}`, goalData);

        if (response.data.success) {
          toast.success("Goal updated successfully");
          
          // Reset form
          setGoalForm({
            title: "",
            description: "",
            type: "",
            targetValue: "",
            unit: "",
            startDate: "",
            endDate: "",
          });

          // Reset editing state
          setEditingGoal(null);
          setShowGoalDrawer(false);
          setGoalDrawerMode(null);

          // Refresh goals list and progress
          if (workDetailsMember) {
            await fetchUserGoals(workDetailsMember._id);
            // Clear cache for this goal since it was updated
            if (progressCacheRef.current[editingGoal._id]) {
              delete progressCacheRef.current[editingGoal._id];
            }
            // Progress will be fetched automatically by useEffect when userGoals updates
          }

          console.log("Goal updated successfully");
        }
      } catch (error) {
        console.error("Error updating goal:", error);
        // Extract error message from API response
        const errorMessage = 
          error.response?.data?.error?.message || 
          error.response?.data?.message || 
          error.message || 
          "Failed to update goal";
        toast.error(errorMessage);
      } finally {
        setIsUpdatingGoal(false);
      }
    },
    [editingGoal, goalForm, workDetailsMember, fetchUserGoals, api]
  );

  // Handle goal completion
  const handleGoalCompletion = useCallback(
    async (goalId, goalCompleted) => {
      try {
        const response = await api.put(`/api/v1/goals/${goalId}/complete`, {
          goalCompleted: goalCompleted
        });

        if (response.data.success) {
          toast.success(`Goal ${goalCompleted ? 'marked as completed' : 'marked as incomplete'} successfully`);
          // Refresh goals list
          if (workDetailsMember) {
            await fetchUserGoals(workDetailsMember._id);
          }
        }
      } catch (error) {
        console.error("Error updating goal completion:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to update goal completion";
        toast.error(errorMessage);
      }
    },
    [workDetailsMember, fetchUserGoals, api]
  );

  // Open edit drawer with goal data
  const handleOpenEditModal = useCallback((goal) => {
    setEditingGoal(goal);
    
    // Parse endTime string to dayjs object if it exists
    let endTimeValue = null;
    if (goal.endTime) {
      // Parse "2:00 PM" or "14:00" format to dayjs
      const timeStr = goal.endTime;
      const isPM = timeStr.includes('PM') || timeStr.includes('pm');
      const isAM = timeStr.includes('AM') || timeStr.includes('am');
      const timeMatch = timeStr.match(/(\d+):(\d+)/);
      
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        
        if (isPM && hours !== 12) hours += 12;
        if (isAM && hours === 12) hours = 0;
        
        // Create dayjs object with today's date and the parsed time
        endTimeValue = dayjs().hour(hours).minute(minutes).second(0);
      } else {
        // Try 24-hour format
        const parts = timeStr.split(':');
        if (parts.length === 2) {
          const hours = parseInt(parts[0]);
          const minutes = parseInt(parts[1]);
          endTimeValue = dayjs().hour(hours).minute(minutes).second(0);
        }
      }
    }
    
    setGoalForm({
      title: goal.title || "",
      description: goal.description || "",
      type: goal.type || "",
      targetValue: goal.targetValue || "",
      unit: goal.unit || "",
      startDate: goal.startDate || "",
      endDate: goal.endDate || "",
      endTime: endTimeValue,
    });
    setGoalDrawerMode('edit');
    setShowGoalDrawer(true);
  }, []);

  // Toggle position expansion for showing all members
  const handleTogglePositionExpansion = useCallback((positionId, roleName) => {
    setExpandedPositions(prev => ({
      ...prev,
      [positionId]: !prev[positionId]
    }));
  }, []);

  // Show all members for a position in a modal
  const handleShowAllMembers = useCallback((positionId, roleName, members) => {
    // Add a small delay for better UX
    setExpandingPositions(prev => ({ ...prev, [positionId]: true }));
    
    setTimeout(() => {
      setSelectedPositionForMembers({
        positionId,
        roleName,
        members
      });
      setExpandingPositions(prev => ({ ...prev, [positionId]: false }));
    }, 200);
  }, []);

  // Close the all members modal
  const handleCloseAllMembers = useCallback(() => {
    setSelectedPositionForMembers(null);
  }, []);

  // Handle member selection from the modal
  const handleSelectMember = useCallback((member) => {
    setSelectedMember(member);
    setWorkDetailsMember(member);
  }, []);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedPositionForMembers) {
        handleCloseAllMembers();
      }
    };

    if (selectedPositionForMembers) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedPositionForMembers, handleCloseAllMembers]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  /**
   * Renders loading state with skeleton
   */
  const renderLoadingState = () => (
    <PageAnimationWrapper>
      <motion.div
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
        className="p-6 space-y-6"
      >
        {/* Header Skeleton */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <Skeleton.Input active size="large" style={{ width: 150, height: 32 }} />
                <Skeleton.Avatar active size={16} shape="circle" />
              </div>
              <Skeleton active paragraph={{ rows: 1, width: 400 }} title={false} />
            </div>
            <div className="hidden lg:flex items-center space-x-4">
              <Skeleton active paragraph={{ rows: 2, width: 80 }} title={false} />
            </div>
          </div>
        </div>

        {/* Organizational Tree Container Skeleton */}
        <div className={`${treeContainerClass} p-6`}>
          {/* Tree Header Skeleton */}
          <div className="flex items-center justify-between mb-8">
            <Skeleton.Input active size="large" style={{ width: 200, height: 32 }} />
            <div className="flex items-center space-x-4">
              <Skeleton active paragraph={false} title={{ width: 100 }} />
              <Skeleton active paragraph={false} title={{ width: 120 }} />
            </div>
          </div>

          {/* Hierarchy Levels Skeleton */}
          <div className="space-y-8">
            {Array.from({ length: 3 }).map((_, levelIndex) => (
              <div key={levelIndex} className="space-y-4">
                {/* Level Badge Skeleton */}
                <div className="flex justify-center mb-6">
                  <Skeleton.Avatar active size={40} shape="circle" />
                </div>

                {/* Positions Grid Skeleton */}
                <Row gutter={[12, 12]} justify="center">
                  {Array.from({ length: 4 }).map((_, posIndex) => (
                    <Col xs={24} sm={12} lg={8} xl={6} key={posIndex}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: (levelIndex * 4 + posIndex) * 0.05 }}
                      >
                        <Card
                          className="h-full"
                          style={{
                            background: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(15, 23, 42, 0.12)',
                            borderRadius: '12px',
                          }}
                        >
                          <div className="text-center space-y-3">
                            <Skeleton active paragraph={false} title={{ width: '80%', style: { margin: '0 auto' } }} />
                            <Skeleton active paragraph={{ rows: 2, width: '100%' }} title={false} />
                            <div className="flex justify-center space-x-2">
                              <Skeleton.Avatar active size={32} shape="circle" />
                              <Skeleton.Avatar active size={32} shape="circle" />
                              <Skeleton.Avatar active size={32} shape="circle" />
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </PageAnimationWrapper>
  );

  /**
   * Renders empty state when no hierarchy data is available
   */
  const renderEmptyState = () => (
    <div className="text-center py-12">
      <Users className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-sm font-medium text-gray-400">
        No team structure found
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        Set up your team structure in Settings to view the hierarchy.
      </p>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <PageAnimationWrapper>
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="skeleton"
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.2 }}
            className="p-6 space-y-6"
          >
            {/* Header Skeleton */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <Skeleton.Input active size="large" style={{ width: 150, height: 32 }} />
                    <Skeleton.Avatar active size={16} shape="circle" />
                  </div>
                  <Skeleton active paragraph={{ rows: 1, width: 400 }} title={false} />
                </div>
                <div className="hidden lg:flex items-center space-x-4">
                  <Skeleton active paragraph={{ rows: 2, width: 80 }} title={false} />
                </div>
              </div>
            </div>

            {/* Organizational Tree Container Skeleton */}
            <div className={`${treeContainerClass} p-6`}>
              {/* Tree Header Skeleton */}
              <div className="flex items-center justify-between mb-8">
                <Skeleton.Input active size="large" style={{ width: 200, height: 32 }} />
                <div className="flex items-center space-x-4">
                  <Skeleton active paragraph={false} title={{ width: 100 }} />
                  <Skeleton active paragraph={false} title={{ width: 120 }} />
                </div>
              </div>

              {/* Hierarchy Levels Skeleton */}
              <div className="space-y-8">
                {Array.from({ length: 3 }).map((_, levelIndex) => (
                  <div key={levelIndex} className="space-y-4">
                    {/* Level Badge Skeleton */}
                    <div className="flex justify-center mb-6">
                      <Skeleton.Avatar active size={40} shape="circle" />
                    </div>

                    {/* Positions Grid Skeleton */}
                    <Row gutter={[12, 12]} justify="center">
                      {Array.from({ length: 4 }).map((_, posIndex) => (
                        <Col xs={24} sm={12} lg={8} xl={6} key={posIndex}>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: (levelIndex * 4 + posIndex) * 0.05 }}
                          >
                            <Card
                              className="h-full"
                              style={{
                                background: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(15, 23, 42, 0.12)',
                                borderRadius: '12px',
                              }}
                            >
                              <div className="text-center space-y-3">
                                <Skeleton active paragraph={false} title={{ width: '80%', style: { margin: '0 auto' } }} />
                                <Skeleton active paragraph={{ rows: 2, width: '100%' }} title={false} />
                                <div className="flex justify-center space-x-2">
                                  <Skeleton.Avatar active size={32} shape="circle" />
                                  <Skeleton.Avatar active size={32} shape="circle" />
                                  <Skeleton.Avatar active size={32} shape="circle" />
                                </div>
                              </div>
                            </Card>
                          </motion.div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
            className="p-6 space-y-6"
          >
        <div className="relative z-10">
          <style>{glassCardStyles}</style>
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1
                  className={`text-2xl font-bold ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Reports
                </h1>
                <div className="group relative">
                  <Lightbulb className="h-4 w-4 text-yellow-400 cursor-help" />
                  <div
                    className={`absolute left-0 top-6 border rounded-lg p-3 text-xs w-72 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl ${
                      isDark
                        ? "bg-black/90 border-white/20 text-white"
                        : "bg-white/95 border-gray-200 text-gray-900"
                    }`}
                  >
                    <div className="font-semibold text-yellow-400 mb-2">
                      📊 Reports Overview
                    </div>
                    <p>
                      View organizational analytics, team performance metrics,
                      and generate comprehensive reports for insights and
                      decision-making.
                    </p>
                  </div>
                </div>
              </div>
              <p
                className={`text-sm ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                View analytics and generate reports. Analyze team performance,
                organizational structure, and productivity insights.
              </p>
            </div>
            <div className="hidden lg:flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-white">
                  {new Date().toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">
                  {new Date().toLocaleDateString("en-US", { weekday: "long" })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Organizational Tree Container */}
        <div className={treeContainerClass}>
          {/* Corner Accent */}
          {isDark && (
            <>
          <div className="absolute top-0 left-0 w-px h-12 bg-gradient-to-b from-blue-500 to-transparent"></div>
          <div className="absolute top-0 left-0 h-px w-12 bg-gradient-to-r from-blue-500 to-transparent"></div>
            </>
          )}
          {/* Tree Header with Legend and Stats */}
          <div className="flex items-center justify-between mb-8 relative z-10">
            <h2 className={treeTitleClass}>
              Organizational Tree
            </h2>

            {/* Legend and Statistics */}
            <div className={`flex items-center space-x-4 ${treeLegendTextClass}`}>
              {/* Direct/Indirect Legend */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <span className={legendChipDirectClass}>
                    D
                  </span>
                  <span className="text-[10px]">
                    Direct Team Reporting
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className={legendChipIndirectClass}>
                    I
                  </span>
                  <span className="text-[10px]">
                    Indirect Team Reporting
                  </span>
                </div>
              </div>

              {/* Total Members Count */}
              <div className={totalMembersTextClass}>
                <Users size={16} className={isDark ? 'text-gray-300' : 'text-slate-600'} />
                <span>{users.length} Total Members</span>
              </div>
            </div>
          </div>

          {/* Search Component */}
          <MemberHierarchySearch
            users={users}
            hierarchy={hierarchy}
            isDark={isDark}
            onFilteredDataChange={({ filteredHierarchy: filtered, searchTerm, matchingMembersCount, hasSearch }) => {
              setFilteredHierarchy(filtered);
              setGlobalSearchTerm(searchTerm);
            }}
            placeholder="Search members by name, email, or position..."
          />

          {/* Hierarchy Content */}
          {filteredHierarchy.length === 0 && globalSearchTerm ? (
            <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              <p className="text-lg mb-2">No members found</p>
              <p className="text-sm">Try adjusting your search terms</p>
            </div>
          ) : filteredHierarchy.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className="relative">
              {/* Hierarchical Tree Structure */}
              <div className="space-y-8">
                {filteredHierarchy.map((level, levelIndex) => (
                  <div key={level.levelId} className="relative">
                    {/* Level Badge - Centered */}
                    <div className="flex justify-center mb-6">
                      <div className={levelBadgeClass}>
                        <span className={`text-sm font-bold ${isDark ? 'text-blue-400' : 'text-indigo-600'}`}>
                          {level.levelId}
                        </span>
                      </div>
                    </div>

                    {/* Positions Grid Layout */}
                    <div className="relative">
                      <div className="flex justify-center items-center">
                        {/* Responsive Grid Container */}
                        <div
                          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-3"
                          style={{
                            gridTemplateColumns: `repeat(${Math.min(
                              level.positions.length,
                              6
                            )}, minmax(0, 1fr))`,
                            maxWidth: `${
                              Math.min(level.positions.length, 6) * 220
                            }px`,
                          }}
                        >
                          {level.positions.map((position, posIndex) => (
                            <div
                              key={position.positionId}
                              className={`${positionCardSurfaceClass} group`}
                            >
                              {/* Position Card */}
                                <div className="text-center">
                                  <div className="flex items-center justify-center mb-2">
                                  <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                      {globalSearchTerm
                                        ? highlightSearchTerm(position.roleName, globalSearchTerm)
                                        : position.roleName}
                                    </h4>
                                  </div>
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                                    {(() => {
                                      const members = getMembersForPosition(
                                        position.roleName
                                      );
                                      if (members.length === 0) {
                                        return (
                                          <div className="text-center py-2">
                                          <span className={emptyMemberBadgeClass}>
                                              No members
                                            </span>
                                          </div>
                                        );
                                      }

                                      const isExpanded = expandedPositions[position.positionId];
                                      const displayMembers = isExpanded ? members : members.slice(0, 3);
                                      const remainingCount = members.length - 3;

                                      return (
                                        <>
                                          {displayMembers.map(
                                            (member, index) => {
                                              const directCount =
                                                getDirectReportsCount(
                                                  member._id
                                                );
                                              const indirectCount =
                                                getIndirectReportsCount(
                                                  member._id
                                                );
                                              return (
                                                <div
                                                  key={member._id}
                                                  className="mb-1 last:mb-0"
                                                >
                                                  <div
                                                  className={`${memberRowClass} group`}
                                                    onClick={() =>
                                                      handleMemberClick(member)
                                                    }
                                                    title={`View details for ${member.firstName} ${member.lastName}`}
                                                  >
                                                  <span className={memberNameClass}>
                                                      {globalSearchTerm
                                                        ? highlightSearchTerm(
                                                            `${member.firstName} ${member.lastName}`,
                                                            globalSearchTerm
                                                          )
                                                        : `${member.firstName} ${member.lastName}`}
                                                    </span>
                                                    <div className="flex items-center space-x-1 ml-2">
                                                      <span
                                                      className={`${directBadgeClass} group-hover:bg-blue-500/50 group-hover:text-white transition-all duration-200`}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleMemberClick(
                                                            member
                                                          );
                                                        }}
                                                      >
                                                        {directCount}
                                                      </span>
                                                      <span className="text-[8px] text-gray-400">
                                                        |
                                                      </span>
                                                      <span
                                                      className={`${indirectBadgeClass} group-hover:bg-purple-500/50 group-hover:text-white transition-all duration-200`}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleMemberClick(
                                                            member
                                                          );
                                                        }}
                                                      >
                                                        {indirectCount}
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            }
                                          )}
                                          {remainingCount > 0 && (
                                            <div className="mt-2 text-center">
                                              {isExpanded ? (
                                                <button
                                                  onClick={() => handleTogglePositionExpansion(position.positionId, position.roleName)}
                                                  className="text-[10px] bg-gray-500/20 text-gray-300 px-2 py-1 rounded-full font-medium hover:bg-gray-500/30 hover:text-white transition-all duration-200"
                                                >
                                                  Show less
                                                </button>
                                              ) : (
                                                <button
                                                  onClick={() => handleShowAllMembers(position.positionId, position.roleName, members)}
                                                  disabled={expandingPositions[position.positionId]}
                                                  className="text-[10px] bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full font-medium hover:bg-purple-500/30 hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                  {expandingPositions[position.positionId] ? (
                                                    <div className="flex items-center space-x-1">
                                                      <div className="w-2 h-2 bg-purple-300 rounded-full animate-pulse"></div>
                                                      <span>Loading...</span>
                                                    </div>
                                                  ) : (
                                                    `+${remainingCount} more`
                                                  )}
                                                </button>
                                              )}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={summaryCardClass}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Total Members</p>
                <p className="text-3xl font-bold text-white">{users.length}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users size={20} className="text-blue-500" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-zinc-500">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                Active
              </div>
            </div>
          </div>

          <div className={summaryCardClass}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Total Levels</p>
                <p className="text-3xl font-bold text-white">
                  {hierarchy.length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <UserCheck size={20} className="text-green-500" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-zinc-500">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                Structured
              </div>
            </div>
          </div>

          <div className={summaryCardClass}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Total Positions</p>
                <p className="text-3xl font-bold text-white">
                  {hierarchy.reduce(
                    (total, level) => total + getLevelPositionCount(level),
                    0
                  )}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <User size={20} className="text-purple-500" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-zinc-500">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-1"></div>
                Allocated
              </div>
            </div>
          </div>
        </div>

        {/* Slide-in Panel - 70% width from right side */}
        {showPanel && selectedMember && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowPanel(false);
              }
            }}
          >
            <div
              className="bg-black border-l border-zinc-800 shadow-2xl overflow-hidden
                          transition-all duration-500 ease-out h-screen w-[70%] flex flex-col relative"
            >
              {/* Header */}
              <div className={panelHeaderSurfaceClass}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-white">
                        {selectedMember.firstName} {selectedMember.lastName}
                      </h3>
                      <button
                        onClick={() => handleViewWork(selectedMember)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          workDetailsMember &&
                          workDetailsMember._id === selectedMember._id
                            ? "bg-white text-black" // Selected state
                            : "bg-white/10 text-white hover:bg-white/20" // Normal state
                        }`}
                      >
                        View Work
                      </button>
                    </div>
                    <p className="text-sm text-gray-400">
                      {selectedMember.position}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPanel(false)}
                    className={modalCloseButtonClass}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content - Side by Side Layout */}
              <div className="flex-1 flex overflow-hidden min-h-0">
                {/* Left Side - Team Reporting */}
                <div className={teamPanelLeftClass}>
                  {/* Fixed Header Section */}
                  <div className="p-6 flex-shrink-0">
                    {/* Section Header */}
                    <div className="mb-6">
                      <h3 className={teamPanelHeaderTitleClass}>
                        Team Reporting
                      </h3>
                      <p className={teamPanelHeaderSubtitleClass}>
                        Direct & Indirect Reports
                      </p>
                    </div>

                    {/* Team Reporting Tabs */}
                    <div className={`flex border-b mb-6 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          if (teamTab !== "direct") {
                            setTeamTab("direct");
                          }
                        }}
                        className={teamTabButtonClass(teamTab === "direct")}
                      >
                        Direct Reporting ({selectedMemberDirectCount})
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          if (teamTab !== "indirect") {
                            setTeamTab("indirect");
                          }
                        }}
                        className={teamTabButtonClass(teamTab === "indirect")}
                      >
                        Indirect Reporting ({selectedMemberIndirectCount})
                      </button>
                    </div>

                    {/* Search Bar */}
                    <div className="mb-6">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                        </div>
                        <input
                          type="text"
                          placeholder="Search by name, email, or position..."
                          value={memberSearchTerm}
                          onChange={(e) => setMemberSearchTerm(e.target.value)}
                          className={searchInputClass}
                        />
                        {memberSearchTerm && (
                          <button
                            onClick={() => setMemberSearchTerm("")}
                            className={searchClearButtonClass}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {memberSearchTerm && (
                        <div className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          Searching for: "{memberSearchTerm}"
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scrollable Tab Content */}
                  <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
                    {teamTab === "direct" ? (
                      <div className="space-y-2">
                        {(() => {
                          const allDirectReports = getDirectReports(
                            selectedMember._id
                          );
                          const filteredDirectReports = allDirectReports.filter(
                            (report) =>
                              `${report.firstName} ${report.lastName}`
                                .toLowerCase()
                                .includes(memberSearchTerm.toLowerCase()) ||
                              report.email
                                .toLowerCase()
                                .includes(memberSearchTerm.toLowerCase()) ||
                              report.position
                                .toLowerCase()
                                .includes(memberSearchTerm.toLowerCase())
                          );

                          if (filteredDirectReports.length === 0) {
                            return memberSearchTerm ? (
                              <div className={emptyStateCardClass}>
                                <p className="text-sm">
                                  No direct reports found matching "
                                  {memberSearchTerm}"
                                </p>
                                <p className="text-xs mt-1">
                                  Try searching by name, email, or position
                                </p>
                              </div>
                            ) : (
                              <div className={emptyStateCardClass}>
                                <p className="text-sm">
                                  No direct reports
                                </p>
                              </div>
                            );
                          }

                          return filteredDirectReports.map((report) => {
                            const isSelected = (workDetailsMember && workDetailsMember._id === report._id) ||
                                              (selectedMember && selectedMember._id === report._id);
                            const directCount = getDirectReportsCount(report._id);
                            const indirectCount = getIndirectReportsCount(report._id);
                            
                            return (
                              <Card
                              key={report._id}
                              onClick={() => handleViewWork(report)}
                                hoverable
                                className="cursor-pointer transition-all"
                                style={{
                                  background: isDark 
                                    ? (isSelected ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.05)')
                                    : (isSelected ? '#f8f9ff' : '#ffffff'),
                                  border: isSelected 
                                    ? (isDark ? '2px solid rgba(99, 102, 241, 0.5)' : '2px solid rgba(99, 102, 241, 0.3)')
                                    : isDark 
                                      ? '1px solid rgba(255, 255, 255, 0.1)' 
                                      : '1px solid rgba(0, 0, 0, 0.08)',
                                  borderRadius: '12px',
                                  boxShadow: isDark 
                                    ? (isSelected ? '0 4px 12px rgba(99, 102, 241, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.3)')
                                    : (isSelected ? '0 4px 12px rgba(99, 102, 241, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'),
                                }}
                                bodyStyle={{ padding: '16px' }}
                              >
                                {/* Header: Name and Level */}
                              <div className="flex items-center justify-between mb-2">
                                  <Text 
                                    strong 
                                    style={{ 
                                      fontSize: '18px',
                                      fontWeight: 600,
                                      color: isDark ? '#ffffff' : '#1e293b',
                                      margin: 0,
                                      lineHeight: '1.4'
                                    }}
                                  >
                                  {highlightSearchTerm(
                                    `${report.firstName} ${report.lastName}`,
                                    memberSearchTerm
                                  )}
                                  </Text>
                                  <div
                                    style={{
                                      background: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                                      borderRadius: '20px',
                                      padding: '4px 12px',
                                      border: 'none'
                                    }}
                                  >
                                    <Text 
                                      style={{ 
                                        fontSize: '12px',
                                        color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#475569',
                                        fontWeight: 500
                                      }}
                                    >
                                  Level {getUserLevel(report.position)}
                                    </Text>
                              </div>
                                </div>
                                
                                {/* Role */}
                                <Text 
                                  style={{ 
                                    fontSize: '14px',
                                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#475569',
                                    display: 'block',
                                    marginBottom: '16px',
                                    lineHeight: '1.5'
                                  }}
                                >
                                {highlightSearchTerm(
                                  report.position,
                                  memberSearchTerm
                                )}
                                </Text>
                                
                                {/* Bottom: Direct, Indirect, View Work */}
                                <div 
                                  className="flex items-stretch" 
                                  style={{ 
                                    marginTop: '12px',
                                    gap: '10px'
                                  }}
                                >
                                  {/* Direct Count Box */}
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: '8px',
                                      padding: '8px 6px',
                                      flex: '1 1 0',
                                      textAlign: 'center',
                                      boxShadow: isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)',
                                      minHeight: '42px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      minWidth: 0
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#1e40af',
                                        lineHeight: '1',
                                        marginBottom: '3px'
                                      }}
                                    >
                                      {directCount}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      Direct
                                    </div>
                                  </div>
                                  
                                  {/* Indirect Count Box */}
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: '8px',
                                      padding: '8px 6px',
                                      flex: '1 1 0',
                                      textAlign: 'center',
                                      boxShadow: isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)',
                                      minHeight: '42px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      minWidth: 0
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#1e40af',
                                        lineHeight: '1',
                                        marginBottom: '3px'
                                      }}
                                    >
                                      {indirectCount}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      Indirect
                                    </div>
                                  </div>
                                  
                                  {/* View Work Button */}
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewWork(report);
                                    }}
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: '8px',
                                      padding: '8px 6px',
                                      flex: '1 1 0',
                                      textAlign: 'center',
                                      cursor: 'pointer',
                                      boxShadow: isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)',
                                      transition: 'all 0.15s ease',
                                      minHeight: '42px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      minWidth: 0
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#f8fafc';
                                      e.currentTarget.style.boxShadow = isDark 
                                        ? '0 2px 4px rgba(0, 0, 0, 0.4)' 
                                        : '0 2px 4px rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = '#ffffff';
                                      e.currentTarget.style.boxShadow = isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)';
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        lineHeight: '1.2',
                                        marginBottom: '1px'
                                      }}
                                    >
                                      View
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        lineHeight: '1.2'
                                      }}
                                    >
                                      Work
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(() => {
                          const allIndirectReports = getIndirectReports(
                            selectedMember._id
                          );
                          const filteredIndirectReports =
                            allIndirectReports.filter(
                              (report) =>
                                `${report.firstName} ${report.lastName}`
                                  .toLowerCase()
                                  .includes(memberSearchTerm.toLowerCase()) ||
                                report.email
                                  .toLowerCase()
                                  .includes(memberSearchTerm.toLowerCase()) ||
                                report.position
                                  .toLowerCase()
                                  .includes(memberSearchTerm.toLowerCase())
                            );

                          if (filteredIndirectReports.length === 0) {
                            return memberSearchTerm ? (
                              <div className={emptyStateCardClass}>
                                <p className="text-sm">
                                  No indirect reports found matching "
                                  {memberSearchTerm}"
                                </p>
                                <p className="text-xs mt-1">
                                  Try searching by name, email, or position
                                </p>
                              </div>
                            ) : (
                              <div className={emptyStateCardClass}>
                                <p className="text-sm">
                                  No indirect reports
                                </p>
                              </div>
                            );
                          }

                          return filteredIndirectReports.map((report) => {
                            const isSelected = (workDetailsMember && workDetailsMember._id === report._id) ||
                                              (selectedMember && selectedMember._id === report._id);
                            const directCount = getDirectReportsCount(report._id);
                            const indirectCount = getIndirectReportsCount(report._id);
                            
                            return (
                              <Card
                              key={report._id}
                              onClick={() => handleViewWork(report)}
                                hoverable
                                className="cursor-pointer transition-all"
                                style={{
                                  background: isDark 
                                    ? (isSelected ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.05)')
                                    : (isSelected ? '#f8f9ff' : '#ffffff'),
                                  border: isSelected 
                                    ? (isDark ? '2px solid rgba(99, 102, 241, 0.5)' : '2px solid rgba(99, 102, 241, 0.3)')
                                    : isDark 
                                      ? '1px solid rgba(255, 255, 255, 0.1)' 
                                      : '1px solid rgba(0, 0, 0, 0.08)',
                                  borderRadius: '12px',
                                  boxShadow: isDark 
                                    ? (isSelected ? '0 4px 12px rgba(99, 102, 241, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.3)')
                                    : (isSelected ? '0 4px 12px rgba(99, 102, 241, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'),
                                }}
                                bodyStyle={{ padding: '16px' }}
                              >
                                {/* Header: Name and Level */}
                              <div className="flex items-center justify-between mb-2">
                                  <Text 
                                    strong 
                                    style={{ 
                                      fontSize: '18px',
                                      fontWeight: 600,
                                      color: isDark ? '#ffffff' : '#1e293b',
                                      margin: 0,
                                      lineHeight: '1.4'
                                    }}
                                  >
                                  {highlightSearchTerm(
                                    `${report.firstName} ${report.lastName}`,
                                    memberSearchTerm
                                  )}
                                  </Text>
                                  <div
                                    style={{
                                      background: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                                      borderRadius: '20px',
                                      padding: '4px 12px',
                                      border: 'none'
                                    }}
                                  >
                                    <Text 
                                      style={{ 
                                        fontSize: '12px',
                                        color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#475569',
                                        fontWeight: 500
                                      }}
                                    >
                                  Level {getUserLevel(report.position)}
                                    </Text>
                              </div>
                                </div>
                                
                                {/* Role */}
                                <Text 
                                  style={{ 
                                    fontSize: '14px',
                                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#475569',
                                    display: 'block',
                                    marginBottom: '16px',
                                    lineHeight: '1.5'
                                  }}
                                >
                                {highlightSearchTerm(
                                  report.position,
                                  memberSearchTerm
                                )}
                                </Text>
                                
                                {/* Bottom: Direct, Indirect, View Work */}
                                <div 
                                  className="flex items-stretch" 
                                  style={{ 
                                    marginTop: '12px',
                                    gap: '10px'
                                  }}
                                >
                                  {/* Direct Count Box */}
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: '8px',
                                      padding: '8px 6px',
                                      flex: '1 1 0',
                                      textAlign: 'center',
                                      boxShadow: isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)',
                                      minHeight: '42px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      minWidth: 0
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#1e40af',
                                        lineHeight: '1',
                                        marginBottom: '3px'
                                      }}
                                    >
                                      {directCount}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      Direct
                                    </div>
                                  </div>
                                  
                                  {/* Indirect Count Box */}
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: '8px',
                                      padding: '8px 6px',
                                      flex: '1 1 0',
                                      textAlign: 'center',
                                      boxShadow: isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)',
                                      minHeight: '42px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      minWidth: 0
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#1e40af',
                                        lineHeight: '1',
                                        marginBottom: '3px'
                                      }}
                                    >
                                      {indirectCount}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      Indirect
                                    </div>
                                  </div>
                                  
                                  {/* View Work Button */}
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewWork(report);
                                    }}
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: '8px',
                                      padding: '8px 6px',
                                      flex: '1 1 0',
                                      textAlign: 'center',
                                      cursor: 'pointer',
                                      boxShadow: isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)',
                                      transition: 'all 0.15s ease',
                                      minHeight: '42px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      minWidth: 0
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#f8fafc';
                                      e.currentTarget.style.boxShadow = isDark 
                                        ? '0 2px 4px rgba(0, 0, 0, 0.4)' 
                                        : '0 2px 4px rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = '#ffffff';
                                      e.currentTarget.style.boxShadow = isDark 
                                        ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
                                        : '0 1px 2px rgba(0, 0, 0, 0.1)';
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        lineHeight: '1.2',
                                        marginBottom: '1px'
                                      }}
                                    >
                                      View
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#1e40af',
                                        fontWeight: 500,
                                        lineHeight: '1.2'
                                      }}
                                    >
                                      Work
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side - Goals */}
                <div className={teamPanelRightClass}>
                  <div className="flex-1 overflow-y-auto p-6 pb-8 min-h-0">
                    {/* Goals Header */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          {/* User Name - Prominent display */}
                          {workDetailsMember && (
                            <div className="mb-3 p-3 bg-white/5 border border-white/10 rounded-lg">
                              <h2 className="text-2xl font-bold text-white mb-1">
                                {workDetailsMember.firstName}{" "}
                                {workDetailsMember.lastName}
                              </h2>
                              <div className="text-lg text-blue-400 font-medium">
                                {workDetailsMember.position || "Member"}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Add Goal Button & Info Icon - Only show on Goals tab */}
                        {workDetailsMember && workDetailsTab === "goals" && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleOpenAddGoalModal}
                              className="bg-white/10 text-white py-2 px-4 rounded-lg border border-white/20 hover:bg-white/20 transition-colors text-xs font-medium"
                            >
                              + Add New Goal
                            </button>
                            <div className="relative group">
                              <button className="text-gray-400 hover:text-white transition-colors p-1">
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                  />
                                </svg>
                              </button>
                              {/* Tooltip */}
                              <div className="absolute bottom-full right-0 mb-2 px-4 py-3 bg-black border border-white/20 rounded-lg text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-72">
                                <div>
                                  <p className="font-semibold mb-2 text-center text-white">
                                    What are Goals?
                                  </p>
                                  <p className="text-gray-300 leading-relaxed text-left mb-2">
                                    Goals represent behavioral expectations and
                                    best practices for each role within the
                                    organization. They define the quality
                                    standards and professional conduct expected
                                    from team members in their respective
                                    positions.
                                  </p>
                                  <p className="text-gray-300 leading-relaxed text-left text-xs">
                                    <span className="font-medium text-white">
                                      Example:
                                    </span>{" "}
                                    "As a Project Manager, you should maintain
                                    comprehensive documentation, create detailed
                                    task specifications for developers, and
                                    prioritize written communication over verbal
                                    instructions to ensure clarity and
                                    accountability."
                                  </p>
                                </div>
                                {/* Arrow */}
                                <div className="absolute top-full right-6 transform w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Divider Line */}
                      {workDetailsMember && (
                        <div className="border-b border-white/10 mb-6"></div>
                      )}
                    </div>

                    {/* Goals Content */}
                    {workDetailsMember && (
                      <div className="space-y-4">
                        {/* Header with Add Goal Button */}
                        <div className="flex justify-between items-start mb-6">
                          <div>
                            <Text 
                              strong 
                              style={{ 
                                fontSize: '20px',
                                color: isDark ? '#ffffff' : '#1f2937',
                                display: 'block',
                                marginBottom: '4px',
                              }}
                            >
                              Team Goals
                            </Text>
                            <Text 
                              type="secondary"
                              style={{ 
                                fontSize: '13px',
                                color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                              }}
                            >
                              Goals set by other team members
                            </Text>
                          </div>
                          <Button
                            type="primary"
                            icon={<span style={{ fontSize: '16px' }}>+</span>}
                            onClick={handleOpenAddGoalModal}
                            style={{
                              height: '36px',
                              borderRadius: '8px',
                              fontWeight: 500,
                              boxShadow: isDark 
                                ? '0 2px 8px rgba(59, 130, 246, 0.3)' 
                                : '0 2px 8px rgba(59, 130, 246, 0.2)',
                            }}
                          >
                            Add Goal
                          </Button>
                        </div>

                        {/* Date Selector */}
                        <div className="mb-6">
                          <ConfigProvider
                            theme={{
                              algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                            }}
                          >
                            <Card
                              style={{
                                background: isDark 
                                  ? 'rgba(0, 0, 0, 0.3)' 
                                  : '#f8fafc',
                                border: isDark 
                                  ? '1px solid rgba(255, 255, 255, 0.1)' 
                                  : '1px solid rgba(0, 0, 0, 0.06)',
                                borderRadius: '12px',
                              }}
                              bodyStyle={{
                                padding: '12px',
                              }}
                            >
                              <div className="flex items-center space-x-2 overflow-x-auto pb-2">
                              {(() => {
                                const today = new Date();
                                const dates = [];

                                // Generate 15 days (7 before, today, 7 after)
                                for (let i = -7; i <= 7; i++) {
                                  const date = new Date(today);
                                  date.setDate(today.getDate() + i);
                                  dates.push(date);
                                }

                                return dates.map((date, index) => {
                                  const isToday =
                                    date.toDateString() ===
                                    today.toDateString();
                                  const isSelected =
                                    date.toDateString() ===
                                    selectedDate.toDateString();
                                  const dayNumber = date.getDate();
                                  const dayName = date.toLocaleDateString(
                                    "en",
                                    { weekday: "short" }
                                  );

                                  // Simplified status for less colorful appearance
                                  const getStatusColor = (dateIndex) => {
                                    const mockData = [
                                      "active",
                                      "active",
                                      "inactive",
                                      "active",
                                      "active",
                                      "active",
                                      "inactive",
                                      "active", // today
                                      "active",
                                      "active",
                                      "inactive",
                                      "active",
                                      "active",
                                      "active",
                                      "active",
                                    ];
                                    return mockData[dateIndex] || "inactive";
                                  };

                                  const statusColor = getStatusColor(index);
                                    const isActive = statusColor === "active";

                                  return (
                                      <Tooltip 
                                      key={index}
                                        title={format(date, 'MMMM d, yyyy')}
                                      >
                                        <div
                                      onClick={() =>
                                        setSelectedDate(new Date(date))
                                      }
                                          style={{
                                            minWidth: '48px',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            background: isSelected
                                              ? isDark 
                                                ? '#3b82f6' 
                                                : '#3b82f6'
                                          : isToday
                                              ? isDark
                                                ? 'rgba(59, 130, 246, 0.2)'
                                                : 'rgba(59, 130, 246, 0.1)'
                                              : 'transparent',
                                            border: isSelected
                                              ? `2px solid ${isDark ? '#60a5fa' : '#2563eb'}`
                                              : '2px solid transparent',
                                            color: isSelected
                                              ? '#ffffff'
                                              : isToday
                                              ? isDark
                                                ? '#60a5fa'
                                                : '#3b82f6'
                                              : isDark
                                              ? 'rgba(255, 255, 255, 0.7)'
                                              : 'rgba(0, 0, 0, 0.65)',
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isSelected && !isToday) {
                                              e.currentTarget.style.background = isDark 
                                                ? 'rgba(255, 255, 255, 0.05)' 
                                                : 'rgba(0, 0, 0, 0.03)';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isSelected && !isToday) {
                                              e.currentTarget.style.background = 'transparent';
                                            }
                                          }}
                                        >
                                          <div 
                                            style={{ 
                                              fontSize: '10px', 
                                              fontWeight: 600,
                                              textTransform: 'uppercase',
                                              marginBottom: '4px',
                                              opacity: isSelected ? 1 : 0.8,
                                            }}
                                          >
                                        {dayName}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: '14px',
                                              fontWeight: 600,
                                              textAlign: 'center',
                                            }}
                                      >
                                        {dayNumber}
                                          </div>
                                      {/* Status Dot */}
                                      <div
                                            style={{
                                              width: '6px',
                                              height: '6px',
                                              borderRadius: '50%',
                                              margin: '4px auto 0',
                                              background: isActive
                                                ? isSelected
                                                  ? '#ffffff'
                                                  : isDark
                                                  ? '#60a5fa'
                                                  : '#3b82f6'
                                                : isDark
                                                ? 'rgba(255, 255, 255, 0.2)'
                                                : 'rgba(0, 0, 0, 0.2)',
                                            }}
                                          />
                                    </div>
                                      </Tooltip>
                                  );
                                });
                              })()}
                            </div>
                            </Card>
                          </ConfigProvider>
                        </div>
                        {(() => {
                          // Filter goals for the selected user (workDetailsMember)
                          // First filter by targetUserId to show only goals for the selected user
                          const goalsForSelectedUser = userGoals.filter((goal) => {
                            if (!workDetailsMember) return false;
                            
                            // Compare targetUserId with selected user's ID
                            const goalTargetUserId = goal.targetUserId?._id || goal.targetUserId || goal.targetUserId;
                            const selectedUserId = workDetailsMember._id || workDetailsMember.id;
                            
                            // Normalize IDs to strings for comparison
                            const goalTargetUserIdStr = goalTargetUserId ? String(goalTargetUserId) : null;
                            const selectedUserIdStr = selectedUserId ? String(selectedUserId) : null;
                            
                            const matchesUser = goalTargetUserIdStr === selectedUserIdStr;
                            
                            if (!matchesUser) {
                              console.log('🚫 Goal filtered out (wrong user):', {
                                goalTitle: goal.title,
                                goalTargetUserId: goalTargetUserIdStr,
                                selectedUserId: selectedUserIdStr,
                                goalTargetUserIdRaw: goal.targetUserId
                              });
                            }
                            
                            return matchesUser;
                          });

                          // Then filter by date range
                          const filteredGoalsByDate = goalsForSelectedUser.filter((goal) => {
                            if (!goal.startDate || !goal.endDate) return true;
                            
                            // Convert dates to YYYY-MM-DD format for accurate comparison
                            const goalStartDate = goal.startDate.includes('T') ? goal.startDate.split('T')[0] : goal.startDate;
                            const goalEndDate = goal.endDate.includes('T') ? goal.endDate.split('T')[0] : goal.endDate;
                            
                            // Format selected date in local timezone (not UTC) to avoid date shifting
                            const year = selectedDate.getFullYear();
                            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                            const day = String(selectedDate.getDate()).padStart(2, '0');
                            const selectedDateStr = `${year}-${month}-${day}`;
                            
                            // Check if selected date is within goal's date range
                            const isInRange = selectedDateStr >= goalStartDate && selectedDateStr <= goalEndDate;
                            
                            console.log('🎯 Goal Date Filtering:', {
                              goalTitle: goal.title,
                              goalStartDate,
                              goalEndDate,
                              selectedDateStr,
                              isInRange
                            });
                            
                            return isInRange;
                          });

                          // Debug: Show filtering results
                          console.log('📊 Filtering Results:', {
                            selectedUser: workDetailsMember ? `${workDetailsMember.firstName} ${workDetailsMember.lastName}` : 'None',
                            selectedUserId: workDetailsMember?._id,
                            totalGoals: userGoals.length,
                            goalsForSelectedUser: goalsForSelectedUser.length,
                            filteredGoalsByDate: filteredGoalsByDate.length,
                            filteredGoalTitles: filteredGoalsByDate.map(g => g.title)
                          });

                          // Helper function to safely compare user IDs
                          const isSameUser = (id1, id2) => {
                            if (!id1 || !id2) return false;
                            try {
                              let id1Str = null;
                              let id2Str = null;
                              
                              // Handle id1 - could be ObjectId, object with _id, or string
                              if (typeof id1 === 'object' && id1 !== null) {
                                // Check if it's an ObjectId (has toString method) or has _id property
                                if (id1.toString && typeof id1.toString === 'function') {
                                  id1Str = id1.toString();
                                } else if (id1._id) {
                                  id1Str = id1._id.toString ? id1._id.toString() : String(id1._id);
                                } else {
                                  id1Str = String(id1);
                                }
                              } else if (id1) {
                                id1Str = String(id1);
                              }
                              
                              // Handle id2 - could be ObjectId, object with _id, or string
                              if (typeof id2 === 'object' && id2 !== null) {
                                // Check if it's an ObjectId (has toString method) or has _id property
                                if (id2.toString && typeof id2.toString === 'function') {
                                  id2Str = id2.toString();
                                } else if (id2._id) {
                                  id2Str = id2._id.toString ? id2._id.toString() : String(id2._id);
                                } else {
                                  id2Str = String(id2);
                                }
                              } else if (id2) {
                                id2Str = String(id2);
                              }
                              
                              return id1Str && id2Str && id1Str === id2Str;
                            } catch (error) {
                              console.warn('Error comparing user IDs:', error, { id1, id2 });
                              return false;
                            }
                          };

                          // Helper function to check if current user can edit/delete a goal
                          const canEditDeleteGoal = (goal) => {
                            if (!user || !goal) return false;
                            
                            // Get user ID - handle both _id and id
                            const userId = user._id || user.id;
                            if (!userId) return false;
                            
                            // If I created the goal, I can edit/delete
                            let createdById = null;
                            if (typeof goal.createdBy === 'object' && goal.createdBy !== null) {
                              // createdBy is populated, so it has _id property
                              createdById = goal.createdBy._id || goal.createdBy.id;
                            } else {
                              // createdBy is a string ID
                              createdById = goal.createdBy;
                            }
                            
                            if (createdById && isSameUser(createdById, userId)) {
                              console.log('✅ User can edit/delete - they created this goal', {
                                goalTitle: goal.title,
                                createdById,
                                userId
                              });
                              return true;
                            }
                            
                            // If I'm admin/manager/team_lead, I can edit/delete any goal
                            // (Backend will enforce hierarchy checks, but UI shows buttons for admins/managers)
                            if (user.role === 'main_admin' || user.role === 'org_admin' || user.role === 'manager' || user.role === 'team_lead') {
                              console.log('✅ User can edit/delete - admin/manager role', {
                                goalTitle: goal.title,
                                userRole: user.role
                              });
                              return true;
                            }
                            
                            // Regular users can only edit/delete goals they created
                            console.log('❌ User cannot edit/delete this goal', {
                              goalTitle: goal.title,
                              createdById,
                              userId,
                              userRole: user.role
                            });
                            return false;
                          };

                          // Separate goals by creator (handle both object and string ID)
                          const isGoalCreatedByUser = (goal) => {
                            if (!goal || !user) return false;
                            
                            // Get user ID - handle both _id and id
                            const userId = user._id || user.id;
                            if (!userId) return false;
                            
                            // Handle different formats of createdBy
                            let createdById = null;
                            
                            if (!goal.createdBy) {
                              return false;
                            }
                            
                            // If createdBy is an object (populated), get _id from it
                            if (typeof goal.createdBy === 'object' && goal.createdBy !== null) {
                              // createdBy is populated, so it has _id property
                              createdById = goal.createdBy._id || goal.createdBy.id;
                            } else {
                              // createdBy is a string ID
                              createdById = goal.createdBy;
                            }
                            
                            if (!createdById) return false;
                            
                            // Convert both to strings for comparison
                            // Handle ObjectId objects by calling toString() if available
                            let createdByIdStr = null;
                            if (createdById && typeof createdById === 'object' && createdById.toString) {
                              createdByIdStr = createdById.toString();
                            } else {
                              createdByIdStr = String(createdById);
                            }
                            
                            let userIdStr = null;
                            if (userId && typeof userId === 'object' && userId.toString) {
                              userIdStr = userId.toString();
                            } else {
                              userIdStr = String(userId);
                            }
                            
                            const result = createdByIdStr === userIdStr;
                            
                            // Debug logging
                            console.log('🔍 Goal filtering:', {
                              goalTitle: goal.title,
                              createdById: createdById,
                              createdByIdStr: createdByIdStr,
                              userId: userId,
                              userIdStr: userIdStr,
                              isSame: result,
                              createdByType: typeof goal.createdBy,
                              createdBy: goal.createdBy,
                              user: user
                            });
                            
                            return result;
                          };

                          // Debug: Log before filtering
                          console.log('📋 Before filtering goals:', {
                            totalGoals: filteredGoalsByDate.length,
                            userId: user?._id || user?.id,
                            user: user,
                            sampleGoal: filteredGoalsByDate[0] ? {
                              title: filteredGoalsByDate[0].title,
                              createdBy: filteredGoalsByDate[0].createdBy,
                              createdByType: typeof filteredGoalsByDate[0].createdBy,
                              createdById: filteredGoalsByDate[0].createdBy?._id || filteredGoalsByDate[0].createdBy
                            } : null
                          });

                          const myGoals = filteredGoalsByDate.filter(isGoalCreatedByUser);
                          const otherGoals = filteredGoalsByDate.filter((goal) => !isGoalCreatedByUser(goal));
                          // Combine all goals into a single list
                          const allGoals = [...filteredGoalsByDate];
                          
                          console.log('✅ After filtering:', {
                            myGoalsCount: myGoals.length,
                            otherGoalsCount: otherGoals.length,
                            allGoalsCount: allGoals.length,
                            myGoalsTitles: myGoals.map(g => g.title),
                            otherGoalsTitles: otherGoals.map(g => g.title)
                          });

                          return (
                            <div key="goals-container">
                              {/* Progress Loading Indicator */}
                              {goalsProgressLoading && filteredGoalsByDate.length > 0 && (
                                <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                  <div className="flex items-center gap-2 text-sm">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                    <span className="text-blue-700 dark:text-blue-300">Updating progress for selected date...</span>
                                  </div>
                                </div>
                              )}

                              {/* Unified Goals List */}
                              {allGoals.length > 0 && (
                                <div className="mb-6">
                                  <AnimatePresence>
                                    <motion.div 
                                      key="goals-list"
                                      className="space-y-3"
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      {allGoals.map((goal, index) => {
                                      // Check if this is a goal created by the user
                                      const isMyGoal = isGoalCreatedByUser(goal);
                                      
                                      // Calculate progress for selected date (for my goals)
                                      const getProgressForDate = (
                                        goalId,
                                        date
                                      ) => {
                                        const daysSinceStart = Math.floor(
                                          (date - new Date(goal.startDate)) /
                                            (1000 * 60 * 60 * 24)
                                        );
                                        const totalDays = Math.floor(
                                          (new Date(goal.endDate) -
                                            new Date(goal.startDate)) /
                                            (1000 * 60 * 60 * 24)
                                        );

                                        if (daysSinceStart < 0) return 0;
                                        if (daysSinceStart > totalDays)
                                          return goal.targetValue;

                                        // Mock progressive increase
                                        const baseProgress = Math.min(
                                          (daysSinceStart / totalDays) *
                                            goal.targetValue *
                                            0.7,
                                          goal.targetValue
                                        );
                                        const randomVariation =
                                          Math.random() *
                                          0.3 *
                                          goal.targetValue;
                                        return Math.min(
                                          baseProgress + randomVariation,
                                          goal.targetValue
                                        );
                                      };

                                      const currentProgress = isMyGoal
                                        ? getProgressForDate(
                                          goal._id,
                                          selectedDate
                                          )
                                        : 0;
                                      const calculatedProgress = isMyGoal
                                        ? Math.min(
                                        (currentProgress / goal.targetValue) *
                                          100,
                                        100
                                          )
                                        : 0;

                                      const progressPercentage = goal.progress?.completionPercentage || (isMyGoal ? calculatedProgress : 0);
                                      const getProgressColor = () => {
                                        if (progressPercentage >= 80) return '#52c41a';
                                        if (progressPercentage >= 50) return '#faad14';
                                        return '#ff4d4f';
                                      };
                                      const getStatusTag = () => {
                                        if (progressPercentage >= 80) return { color: 'success', text: 'On Track' };
                                        if (progressPercentage >= 50) return { color: 'warning', text: 'In Progress' };
                                        return { color: 'error', text: 'Needs Attention' };
                                      };
                                      const statusTag = getStatusTag();
                                      const canEditDelete = canEditDeleteGoal(goal);

                                      // Check if goal is missed
                                      const isGoalMissed = () => {
                                        // If goal is completed, it's not missed
                                        if (goal.goalCompleted) return false;
                                        
                                        // If completion percentage is 100%, it's not missed
                                        if (progressPercentage >= 100) return false;
                                        
                                        const now = new Date();
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        
                                        // Parse end date (handle both "2025-01-15" and Date objects)
                                        const endDateStr = goal.endDate.includes('T') ? goal.endDate.split('T')[0] : goal.endDate;
                                        const endDate = new Date(endDateStr);
                                        endDate.setHours(0, 0, 0, 0);
                                        
                                        // If end date is in the future, it's not missed
                                        if (endDate > today) return false;
                                        
                                        // If end date is in the past (before today), it's missed
                                        if (endDate < today) return true;
                                        
                                        // If end date is today, check endTime if it exists
                                        if (endDate.getTime() === today.getTime() && goal.endTime) {
                                          // Parse endTime string (format: "12:00 PM" or "14:00")
                                          const parseTimeToMinutes = (timeStr) => {
                                            if (!timeStr) return null;
                                            
                                            const isPM = timeStr.includes('PM') || timeStr.includes('pm');
                                            const isAM = timeStr.includes('AM') || timeStr.includes('am');
                                            const timeMatch = timeStr.match(/(\d+):(\d+)/);
                                            
                                            if (!timeMatch) {
                                              // Try 24-hour format
                                              const parts = timeStr.split(':');
                                              if (parts.length === 2) {
                                                return parseInt(parts[0]) * 60 + parseInt(parts[1]);
                                              }
                                              return null;
                                            }
                                            
                                            let hours = parseInt(timeMatch[1]);
                                            const minutes = parseInt(timeMatch[2]);
                                            
                                            if (isPM && hours !== 12) hours += 12;
                                            if (isAM && hours === 12) hours = 0;
                                            
                                            return hours * 60 + minutes;
                                          };
                                          
                                          const endTimeMinutes = parseTimeToMinutes(goal.endTime);
                                          if (endTimeMinutes !== null) {
                                            const currentMinutes = now.getHours() * 60 + now.getMinutes();
                                            // If current time is before endTime, it's not missed yet
                                            if (currentMinutes < endTimeMinutes) return false;
                                          }
                                          // If endTime has passed, it's missed
                                          return true;
                                        }
                                        
                                        // If end date is today and no endTime, it's not missed (still has the day)
                                        // Only mark as missed if we're past the end date
                                        return false;
                                      };
                                      
                                      const goalIsMissed = isGoalMissed();

                                      return (
                                        <motion.div
                                          key={goal._id}
                                          initial={{ opacity: 0, y: 20 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -20 }}
                                          transition={{ 
                                            duration: 0.3,
                                            delay: index * 0.05,
                                            ease: "easeOut"
                                          }}
                                        >
                                          <ConfigProvider
                                          theme={{
                                            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                                            token: {
                                              colorBgContainer: isDark ? 'rgba(0, 0, 0, 0.5)' : '#ffffff',
                                              colorBorder: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                                            },
                                          }}
                                        >
                                          <Card
                                            className="mb-3 hover:shadow-lg transition-all duration-300"
                                            style={{
                                              background: isDark 
                                                ? 'rgba(0, 0, 0, 0.5)' 
                                                : '#ffffff',
                                              border: isDark 
                                                ? '1px solid rgba(255, 255, 255, 0.12)' 
                                                : '1px solid rgba(0, 0, 0, 0.08)',
                                              borderRadius: '12px',
                                            }}
                                            bodyStyle={{
                                              padding: '16px',
                                            }}
                                          >
                                            <div className="space-y-3">
                                              {/* Header with Title and Action Buttons */}
                                              <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                  <Text 
                                                    strong 
                                                    className="text-base block mb-2"
                                                    style={{
                                                      color: isDark ? '#ffffff' : '#1f2937',
                                                      fontSize: '16px',
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                  {goal.title}
                                                  </Text>
                                                  <Space size={[8, 8]} wrap>
                                                    <Tag 
                                                      icon={<UserOutlined />} 
                                                      color="blue"
                                                      style={{ margin: 0 }}
                                                    >
                                                      {isSameUser(goal.createdBy?._id || goal.createdBy, user?._id)
                                                        ? 'You' 
                                                        : `${goal.createdBy?.firstName || ''} ${goal.createdBy?.lastName || ''}`.trim()}
                                                    </Tag>
                                                    <Tag 
                                                      icon={<CalendarOutlined />} 
                                                      color="default"
                                                      style={{ margin: 0 }}
                                                    >
                                                      {new Date(goal.startDate).toLocaleDateString()} - {new Date(goal.endDate).toLocaleDateString()}
                                                    </Tag>
                                                    {goal.endTime && (
                                                      <Tag 
                                                        icon={<ClockCircleOutlined />} 
                                                        color="purple"
                                                        style={{ margin: 0 }}
                                                      >
                                                        End: {goal.endTime}
                                                      </Tag>
                                                    )}
                                                    {goal.goalCompleted && (
                                                      <Tag 
                                                        color="success"
                                                        style={{ margin: 0 }}
                                                      >
                                                        ✓ Completed
                                                      </Tag>
                                                    )}
                                                    {goalIsMissed && !goal.goalCompleted && (
                                                      <Tag 
                                                        color="error"
                                                        style={{ margin: 0 }}
                                                      >
                                                        Missed
                                                      </Tag>
                                                    )}
                                                    {/* <Tag 
                                                      color={statusTag.color}
                                                      style={{ margin: 0 }}
                                                    >
                                                      {statusTag.text}
                                                    </Tag> */}
                                                  </Space>
                                  </div>
                                                
                                                {/* Action Buttons - Show Edit/Delete only if user has permission */}
                                                <Space size="small">
                                                  {canEditDelete && (
                                                    <>
                                                      <Tooltip title="Edit goal">
                                                        <Button
                                                          type="text"
                                                          size="small"
                                                          icon={<EditOutlined />}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenEditModal(goal);
                                                          }}
                                                          style={{
                                                            color: isDark ? '#60a5fa' : '#3b82f6',
                                                          }}
                                                        />
                                                      </Tooltip>
                                                      <Tooltip title="Delete goal">
                                                        <Button
                                                          type="text"
                                                          size="small"
                                                          danger
                                                          icon={<DeleteOutlined />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                            handleDeleteGoal(goal._id);
                                                          }}
                                                        />
                                                      </Tooltip>
                                                    </>
                                                  )}
                                                  {/* Always show Complete/Incomplete button */}
                                                  <Tooltip title={goal.goalCompleted ? "Mark as incomplete" : "Mark as completed"}>
                                                    <Button
                                                      type="text"
                                                      size="small"
                                                      icon={<CheckCircleOutlined />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                        handleGoalCompletion(goal._id, !goal.goalCompleted);
                                                      }}
                                                      style={{
                                                        color: goal.goalCompleted 
                                                          ? (isDark ? '#52c41a' : '#52c41a')
                                                          : (isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'),
                                                      }}
                                                    />
                                                  </Tooltip>
                                                </Space>
                                              </div>

                                              {/* Description */}
                                              <Paragraph 
                                                ellipsis={{ rows: 2, expandable: false }}
                                                style={{ 
                                                  color: isDark ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.7)',
                                                  fontSize: '13px',
                                                  marginBottom: 0,
                                                  lineHeight: '1.5',
                                                }}
                                              >
                                              {goal.description}
                                              </Paragraph>

                                              {/* Progress Section */}
                                              {/* <div style={{ paddingTop: '12px' }}>
                                                <Space 
                                                  style={{ 
                                                    width: '100%', 
                                                    justifyContent: 'space-between',
                                                    marginBottom: '8px'
                                                  }}
                                                >
                                                  <Space size="small">
                                                    <AimOutlined 
                                                      style={{ 
                                                        color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)',
                                                        fontSize: '14px'
                                                      }}
                                                    />
                                                    <Text 
                                                      type="secondary" 
                                                      style={{ 
                                                        fontSize: '12px',
                                                        color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                                                      }}
                                                    >
                                                      Target: {goal.targetValue} {goal.unit}
                                                    </Text>
                                                  </Space>
                                                  <Text 
                                                    strong
                                                    style={{ 
                                                      color: getProgressColor(),
                                                      fontSize: '15px',
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    {progressPercentage.toFixed(1)}%
                                                  </Text>
                                                </Space>
                                                <Progress
                                                  percent={progressPercentage}
                                                  strokeColor={{
                                                    '0%': getProgressColor(),
                                                    '100%': getProgressColor(),
                                                  }}
                                                  showInfo={false}
                                                  size="default"
                                                  strokeWidth={8}
                                                  style={{
                                                    marginBottom: 0,
                                                  }}
                                                />
                                                        </div> */}
                                                        </div>
                                          </Card>

                                          {/* Expandable Analysis Section */}
                                          {expandedGoalAnalysis === goal._id && !isCreatingGoal && !showGoalDrawer && (
                                            <Card
                                              className="mt-2"
                                              style={{
                                                background: isDark 
                                                  ? 'rgba(0, 0, 0, 0.6)' 
                                                  : '#f5f5f5',
                                                border: isDark 
                                                  ? '1px solid rgba(255, 255, 255, 0.1)' 
                                                  : '1px solid rgba(0, 0, 0, 0.06)',
                                              }}
                                            >
                                              <GoalAnalysisDisplay goal={goal} />
                                            </Card>
                                          )}
                                        </ConfigProvider>
                                        </motion.div>
                                      );
                                    })}
                                    </motion.div>
                                  </AnimatePresence>
                                </div>
                              )}

                              {/* Show empty state when no goals */}
                              {!goalsLoading && allGoals.length === 0 && (
                                <ConfigProvider
                                  theme={{
                                    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                                  }}
                                >
                                  <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                      <div>
                                        <Text
                                          style={{
                                            color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
                                            fontSize: '14px',
                                          }}
                                        >
                                          No goals found
                                        </Text>
                                        <div style={{ marginTop: '8px' }}>
                                          <Text
                                            type="secondary"
                                            style={{
                                              fontSize: '12px',
                                              color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                            }}
                                          >
                                            Set goals to track performance and progress
                                          </Text>
                                            </div>
                                          </div>
                                    }
                                  >
                                    <Button
                                      type="primary"
                                      icon={<span style={{ fontSize: '16px' }}>+</span>}
                                      onClick={handleOpenAddGoalModal}
                                      style={{
                                        marginTop: '16px',
                                        height: '36px',
                                        borderRadius: '8px',
                                      }}
                                    >
                                      Add First Goal
                                    </Button>
                                  </Empty>
                                </ConfigProvider>
                              )}

                              {/* Loading State - Skeleton */}
                              {goalsLoading && (
                                <div className="space-y-4">
                                  <div className="mb-6">
                                    <Skeleton active paragraph={{ rows: 2 }} />
                                    <div className="space-y-3 mt-4">
                                      {[1, 2, 3].map((i) => (
                                        <Card key={i} className="mb-3">
                                          <Skeleton active avatar paragraph={{ rows: 3 }} />
                                        </Card>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                                    </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Goal Form Drawer */}
        <GoalFormDrawer
          isOpen={showGoalDrawer}
          onClose={() => {
            setShowGoalDrawer(false);
            setGoalDrawerMode(null);
            setEditingGoal(null);
          }}
          mode={goalDrawerMode}
          goalForm={goalForm}
          setGoalForm={setGoalForm}
          onSubmit={goalDrawerMode === 'add' ? handleAddGoal : handleEditGoal}
          isSubmitting={goalDrawerMode === 'add' ? isCreatingGoal : isUpdatingGoal}
          isDark={isDark}
        />

        {/* All Members Modal */}
        {selectedPositionForMembers && (
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseAllMembers}
          >
            <div 
              className={`${modalSurfaceClass} max-w-2xl w-full max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-200`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="p-6 border-b" style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }}>
                <div className="flex items-center justify-between">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    All Members - {selectedPositionForMembers.roleName}
                  </h3>
                  <button
                    onClick={handleCloseAllMembers}
                    className={modalCloseButtonClass}
                  >
                    <X size={20} />
                  </button>
                  </div>
                    </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                {(() => {
                  const filteredModalMembers = selectedPositionForMembers.members.filter((member) => {
                    const searchTerm = membersSearchTerm.toLowerCase();
                    const fullName = `${member.firstName || ''} ${member.lastName || ''}`.toLowerCase();
                    const email = (member.email || '').toLowerCase();
                    return fullName.includes(searchTerm) || email.includes(searchTerm);
                  });

                  if (filteredModalMembers.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <Text type="secondary">No members found</Text>
                      </div>
                    );
                  }

                  return filteredModalMembers.map((member) => {
                    return (
                      <div
                        key={member._id || member.id}
                        className={modalListItemClass}
                        onClick={() => {
                          handleSelectMember(member);
                          handleCloseAllMembers();
                        }}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={modalAvatarClass}>
                            {(member.firstName?.[0] || member.email?.[0] || 'U').toUpperCase()}
                    </div>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {member.firstName} {member.lastName}
                  </div>
                            <div className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {member.email}
                    </div>
                    </div>
                  </div>
                </div>
                    );
                  });
                })()}
              </div>
              <div className="p-4 border-t flex justify-end" style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }}>
                  <button
                  onClick={handleCloseAllMembers}
                  className={modalActionButtonClass}
                >
                  Close
                  </button>
                </div>
            </div>
          </div>
        )}

        {/* All Members Modal */}
        {selectedPositionForMembers && (
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseAllMembers}
          >
            <div 
              className={`${modalSurfaceClass} max-w-2xl w-full max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-200`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="All team members list"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-zinc-700">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>All Team Members</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setMembersSearchTerm('')}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${
                        isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }`}
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleCloseAllMembers}
                      className={modalCloseButtonClass}
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6 max-h-[60vh] overflow-y-auto">
                  <div className="mb-4 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                    </div>
                    <input
                      type="text"
                      value={membersSearchTerm}
                      onChange={(e) => setMembersSearchTerm(e.target.value)}
                      className={searchInputClass}
                      placeholder="Search members..."
                    />
                    {membersSearchTerm && (
                      <button
                        onClick={() => setMembersSearchTerm('')}
                        className={searchClearButtonClass}
                        aria-label="Clear member search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {(() => {
                      const filteredModalMembers = selectedPositionForMembers.members.filter((member) =>
                        `${member.firstName} ${member.lastName}`
                          .toLowerCase()
                          .includes(membersSearchTerm.toLowerCase()) ||
                        member.email
                          .toLowerCase()
                          .includes(membersSearchTerm.toLowerCase())
                      );

                      if (filteredModalMembers.length === 0) {
                        return (
                          <div className={emptyStateCardClass}>
                            <p className="text-sm">
                              No members found{membersSearchTerm ? ` matching "${membersSearchTerm}"` : ''}
                            </p>
                          </div>
                        );
                      }

                      return filteredModalMembers.map((member) => {
                        const directCount = getDirectReportsCount(member._id);
                        const indirectCount = getIndirectReportsCount(member._id);
                        
                        return (
                          <div
                            key={member._id}
                            className={modalListItemClass}
                            onClick={() => {
                              handleMemberClick(member);
                              handleCloseAllMembers();
                            }}
                            title={`View details for ${member.firstName} ${member.lastName}`}
                          >
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <div className={modalAvatarClass}>
                                <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-indigo-600'}`}>
                                  {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                  {member.firstName} {member.lastName}
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{member.email}</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <span className={infoPillClass}>{directCount} direct</span>
                              <span className={infoPillClass}>{indirectCount} indirect</span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                <div className="border-t border-zinc-700 p-4 flex justify-end space-x-3">
                  <button
                    onClick={handleCloseAllMembers}
                    className={modalActionButtonClass}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageAnimationWrapper>
  );
};

export default Reports;
